import React, { useEffect, useState, useRef } from 'react';
import { agentsAPI, knowledgeAPI, helpCenterAPI, knowledgeAIAPI, shopifyAPI } from '../services/api';
import type { ArticleSuggestion } from '../services/api';
import { Agent, KnowledgeSource, HelpCenterCollection } from '../types';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling } from '../contexts/BillingContext';
import { useBillingMode } from '../hooks/useBillingMode';
import { slugifyTyping, slugifyStrict } from '../utils/slug';
import type { Plan } from '../types/billing';
import { isPooledBilling } from '../types/billing';
import { Upload, Link as LinkIcon, FileText, Trash2, Plus, BookOpen, Eye, Edit2, X, Loader2, Image, Video, HelpCircle, Globe, FileEdit, CheckSquare, Square, Info, Clipboard, ChevronDown, ChevronRight, Sparkles, RotateCcw, CheckCircle, Layers, MessageCircle, ThumbsUp, ThumbsDown, Search, ArrowRightLeft, AlertTriangle, Store } from 'lucide-react';
import CollectionManager from '../components/help_center/CollectionManager';
import FilePreview from '../components/help_center/FilePreview';
import { streamSSE } from '../utils/streamSSE';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import ConfirmModal from '../components/ConfirmModal';
import { useToast } from '../contexts/ToastContext';
import RichTextEditor from '../components/RichTextEditor';
import { serverIsoToMillis, formatServerDate, NO_DATE } from '../utils/datetime';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { collectAppendedArticleVideos } from '../utils/articleVideos';
import {
  VIDEO_ANALYSIS_PLAN_CAP_MB,
  VIDEO_ANALYSIS_NEXT_TIER,
  VIDEO_ANALYSIS_DEFAULT_QUOTA,
  PLAN_LABELS,
  isVideoFile,
  isYouTubeUrl,
  youtubeEmbedHtml,
  uploadedVideoEmbedHtml,
  buildAnalyzedArticleHtml,
  formatAnalyzeError,
  withPosterFrame,
} from '../utils/videoAnalysis';

/**
 * Load-time only: append any sidecar videos missing from `html` as editable
 * <video> blocks at the bottom. Dedup is handled by
 * collectAppendedArticleVideos, so re-saving + reopening after staff drags
 * a video inline will not re-append it.
 */
const injectMissingSidecarVideos = (
  html: string,
  source: { media_videos?: any[]; video_urls?: string[] }
): string => {
  const missing = collectAppendedArticleVideos(html, source);
  if (!missing.length) return html;
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const blocks = missing
    .map((v) => {
      const encoded = v.url.replace(/ /g, '%20');
      const caption = v.label
        ? `<p><em>${escape(v.label)}</em></p>`
        : '';
      return `<p><video controls playsinline preload="metadata" src="${escape(withPosterFrame(encoded))}"></video></p>${caption}`;
    })
    .join('');
  return html + blocks;
};

// --- Article HTML helpers (Phase 1 WYSIWYG) ---
// Video-analysis helpers (file caps, isVideoFile, isYouTubeUrl, etc.) live
// in `utils/videoAnalysis.ts` so the Insert-Video dialog in RichTextEditor
// can share them. See that file for §B.1/§B.2/§B.5/§B.7 LOCKED constants.
const isHtmlContent = (s: string): boolean => {
  const trimmed = (s || '').trim();
  return trimmed.startsWith('<');
};

/** Replace [VIDEO: title](url) markdown tokens with raw HTML before markdown conversion. */
const preprocessVideoTokens = (md: string): string => {
  if (!md) return md;
  const videoRegex = /\[VIDEO:\s*([^\]]*)\]\(([^)]+)\)/g;
  return md.replace(videoRegex, (_full, _title, url) => {
    const u = String(url).trim();
    let embedUrl: string | null = null;
    try {
      if (u.includes('youtube.com/watch')) {
        const v = new URLSearchParams(new URL(u).search).get('v');
        embedUrl = v ? `https://www.youtube.com/embed/${v}?rel=0` : null;
      } else if (u.includes('youtu.be/')) {
        const id = u.split('youtu.be/')[1]?.split('?')[0];
        embedUrl = id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
      }
    } catch {}
    if (embedUrl) {
      return `<iframe src="${embedUrl}" frameborder="0" allowfullscreen="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" class="w-full aspect-video rounded-lg my-2"></iframe>`;
    }
    return `<video src="${withPosterFrame(u)}" controls="controls" playsinline preload="metadata" class="max-w-full rounded-lg my-2"></video>`;
  });
};

/** Convert markdown (with VIDEO tokens) to HTML suitable for RichTextEditor. */
const markdownToEditorHtml = (md: string): string => {
  if (!md) return '';
  if (isHtmlContent(md)) return md;
  const preprocessed = preprocessVideoTokens(md);
  try {
    // marked.parse is sync when async:false (default for v4+; v12 keeps sync signature)
    const out = marked.parse(preprocessed, { async: false }) as string;
    return out;
  } catch (e) {
    console.error('markdown→html conversion failed', e);
    return preprocessed;
  }
};

/** True when editor HTML has no meaningful text/media content. */
const isEditorHtmlEmpty = (html: string): boolean => {
  if (!html) return true;
  // Has a block-level media element? Then it's not empty.
  if (/<(img|video|iframe|source)\b/i.test(html)) return false;
  // Strip tags and whitespace-entities, compare.
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return text.length === 0;
};

/** Sanitize HTML for safe rendering in the preview/view modal. */
const sanitizeArticleHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ['video', 'source', 'iframe'],
    ADD_ATTR: [
      'controls', 'preload', 'allowfullscreen', 'frameborder', 'allow', 'src', 'target', 'rel',
      // Preserve resize / alignment output from tiptap-extension-resize-image
      'style', 'class', 'width', 'height', 'align', 'data-align',
    ],
  });

// Strict form, for values derived from a FINISHED string (an agent name, an
// article title). The article-slug INPUT below must not use this on every
// keystroke — it would eat the hyphen as you type it. See utils/slug.ts.
const slugify = slugifyStrict;

const KnowledgeHub: React.FC = () => {
  const { activeWorkspace } = useWorkspace();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [knowledge, setKnowledge] = useState<KnowledgeSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [scrapingUrls, setScrapingUrls] = useState(false);
  const [urls, setUrls] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualTitle, setManualTitle] = useState('');
  const [manualContent, setManualContent] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualAiGenerateDescription, setManualAiGenerateDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [editAiGenerateDescription, setEditAiGenerateDescription] = useState(false);
  const [viewingArticle, setViewingArticle] = useState<KnowledgeSource | null>(null);
  const [viewingPreview, setViewingPreview] = useState<{
    preview_type: 'markdown' | 'text' | 'table';
    content: string | null;
    preview_data: { columns: string[]; rows: string[][] } | null;
  } | null>(null);
  const [editingArticle, setEditingArticle] = useState<KnowledgeSource | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<{ id: string, title: string } | null>(null);
  // Move / Copy Article
  const [transferArticle, setTransferArticle] = useState<{ id: string, title: string } | null>(null);
  const [transferAction, setTransferAction] = useState<'move' | 'copy'>('copy');
  const [transferTargetAgent, setTransferTargetAgent] = useState<string>('');
  const [transferLoading, setTransferLoading] = useState(false);
  // Bulk Move / Copy
  const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
  const [bulkTransferAction, setBulkTransferAction] = useState<'move' | 'copy'>('copy');
  const [bulkTransferTargetAgent, setBulkTransferTargetAgent] = useState<string>('');
  const [bulkTransferLoading, setBulkTransferLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  // Bug 1 — default to draft (per CEO direction: accidental publishes are
  // the bug). The two buttons in the create form below also pass an
  // explicit status override into handleCreateManual to bypass the
  // setState-then-call-in-same-tick stale-closure issue that previously
  // made BOTH buttons POST whatever `manualStatus` was at render time.
  const [manualStatus, setManualStatus] = useState<'draft' | 'published'>('draft');
  const [editStatus, setEditStatus] = useState<'draft' | 'published'>('published');
  // In-flight save flags so we can disable the Save buttons + show a spinner
  // while the request is processing. With ai_generate_description=true the
  // BE makes a synchronous Gemini call (~3-5s); without visual feedback users
  // double-click, abandon, or assume the toggle broke.
  const [manualSaving, setManualSaving] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkCollectionPicker, setShowBulkCollectionPicker] = useState(false);
  const [bulkCollections, setBulkCollections] = useState<HelpCenterCollection[]>([]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [fileMetadataList, setFileMetadataList] = useState<{ title: string; description: string; status: 'draft' | 'published'; skip_summary: boolean; analyze_video: boolean }[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [submittingUpload, setSubmittingUpload] = useState(false);

  // ── Video analysis (Plan-VideoAnalysis.md §C.2 frontend) ───────────────────
  // URL-paste flow: single global toggle that activates when the textarea
  // contains at least one YouTube URL. On submit, the FIRST YouTube URL is
  // analyzed and its resulting article opens in edit mode pre-filled.
  const [urlAnalyzeVideo, setUrlAnalyzeVideo] = useState(false);
  // Progress overlay surfaced for the duration of the sync analyze HTTP call
  // (~30s typical per Plan §B.6). Blocks dismissal of the upload modal.
  const [analyzingVideo, setAnalyzingVideo] = useState(false);
  const { billing } = useBilling();
  const showLegacyCredits = useBillingMode() === 'legacy';
  const pendingImagesRef = useRef<Map<string, File>>(new Map());
  // Track S3 keys uploaded through the editor so we can cleanup orphans on cancel/save.
  // One per open modal (Create + Edit).
  const createPendingMediaKeysRef = useRef<Set<string>>(new Set());
  const editPendingMediaKeysRef = useRef<Set<string>>(new Set());
  const editingArticleRef = useRef<KnowledgeSource | null>(null);
  const { showToast } = useToast();

  // Help Center meta edit state
  const [hcCollections, setHCCollections] = useState<HelpCenterCollection[]>([]);
  const [editHCCollectionId, setEditHCCollectionId] = useState('');
  const [editHCSlug, setEditHCSlug] = useState('');
  const [editHCMetaTitle, setEditHCMetaTitle] = useState('');
  const [editHCMetaDesc, setEditHCMetaDesc] = useState('');
  const [editSeoExpanded, setEditSeoExpanded] = useState(false);
  const [hcCollectionsLoading, setHCCollectionsLoading] = useState(false);
  const [copiedArticleLink, setCopiedArticleLink] = useState(false);

  // Shopify Default articles — Import UI state (Plan-ShopifyDefaultArticles.md §5,
  // pivoted 2026-06-15 from auto-sync → manual Import per dropdown agent).
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importing, setImporting] = useState(false);
  // Cap-exceeded banner: set true when latest import response says pages_capped.
  // Dismiss state lives in localStorage per workspace_id so it persists across reloads.
  const [pagesCappedActive, setPagesCappedActive] = useState(false);
  const capDismissKey = activeWorkspace?.id
    ? `shopify_pages_capped_dismissed_${activeWorkspace.id}`
    : null;
  const [capDismissed, setCapDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (!capDismissKey) return false;
    try {
      return window.localStorage.getItem(capDismissKey) === '1';
    } catch {
      return false;
    }
  });
  // Re-read dismiss state on workspace switch so the banner respects per-workspace
  // dismissal across the SPA navigation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!capDismissKey) {
      setCapDismissed(false);
      return;
    }
    try {
      setCapDismissed(window.localStorage.getItem(capDismissKey) === '1');
    } catch {
      setCapDismissed(false);
    }
  }, [capDismissKey]);

  const currentAgent = agents.find(a => a.agent_id === selectedAgent);
  // Stored slug first (see Agent.help_center_slug). Feeds the public article
  // URL preview below, which was showing a name-derived path that drifted on
  // every rename. Fallback mirrors the server resolver for legacy agents.
  const currentAgentSlug = currentAgent?.help_center_slug || slugify(currentAgent?.config.agent_name ?? '');

  // 🚨 The public Help Center is SERVER-rendered by the backend (Jinja, see
  // api/help_center_pages.py) and served from the API origin — help.prodagi.ai
  // in prod. The dashboard runs on a DIFFERENT host (app.prodagi.ai), and the
  // SPA has no `/help/:workspaceSlug/:agentSlug` route at all. Building these
  // links from `window.location.origin` therefore produced app.prodagi.ai/help/…
  // — a host that does not serve help center pages, so the copied link 404'd
  // every time. Same base HelpCenterStudio uses.
  const publicHelpCenterOrigin = import.meta.env.VITE_API_URL || 'http://localhost:8000';
  // Strict form: the preview (and the copied link) must show what will actually
  // be saved, not a mid-typing value like "getting-started-".
  const editHCSlugStrict = slugifyStrict(editHCSlug);
  const articlePublicUrl = activeWorkspace?.slug && currentAgentSlug && editHCSlugStrict
    ? `${publicHelpCenterOrigin}/help/${activeWorkspace.slug}/${currentAgentSlug}/${editHCSlugStrict}`
    : '';

  // ── AI features ──────────────────────────────────────────────────────────
  const [hubTab, setHubTab] = useState<'articles' | 'collections' | 'suggestions'>('articles');

  // Suggestions tab
  const [suggestions, setSuggestions] = useState<ArticleSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  // Create form — AI gen panel
  const [showCreateAIPanel, setShowCreateAIPanel] = useState(false);
  const [createAITone, setCreateAITone] = useState<'friendly' | 'formal' | 'concise'>('friendly');
  const [createAIContext, setCreateAIContext] = useState('');
  const [createAIConversationId, setCreateAIConversationId] = useState<string | null>(null);
  const [createAIGenerating, setCreateAIGenerating] = useState(false);

  // Edit modal — AI gen panel + improve + SEO
  const [showEditAIPanel, setShowEditAIPanel] = useState(false);
  const [editAITone, setEditAITone] = useState<'friendly' | 'formal' | 'concise'>('friendly');
  const [editAIContext, setEditAIContext] = useState('');
  const [editAIGenerating, setEditAIGenerating] = useState(false);
  const [showImproveDropdown, setShowImproveDropdown] = useState(false);
  const [improving, setImproving] = useState(false);
  const [editSeoGenerating, setEditSeoGenerating] = useState(false);

  const improveDropdownRef = useRef<HTMLDivElement>(null);

  // Help Center Generator

  // Article search
  const [searchQuery, setSearchQuery] = useState('');

  // Split into Articles
  const [splitting, setSplitting] = useState(false);

  // AI Preview (accept/reject before applying)
  const [createAIPreview, setCreateAIPreview] = useState<{ content: string; title?: string; collection?: string } | null>(null);
  const [editAIPreview, setEditAIPreview] = useState<{ content: string; title?: string; collection?: string } | null>(null);
  const [previousCreateContent, setPreviousCreateContent] = useState('');
  const [previousEditContent, setPreviousEditContent] = useState('');
  const createAccumRef = useRef('');
  const editAccumRef = useRef('');

  // Create form — improve dropdown
  const [showCreateImproveDropdown, setShowCreateImproveDropdown] = useState(false);
  const [createImproving, setCreateImproving] = useState(false);
  const createImproveDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      fetchKnowledge();
    }
  }, [selectedAgent]);

  const fetchAgents = async () => {
    try {
      const response = await agentsAPI.list();
      setAgents(response.data);
      if (response.data.length > 0) {
        setSelectedAgent(response.data[0].agent_id);
      }
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    }
  };

  const fetchKnowledge = async () => {
    if (!selectedAgent) return;
    setLoading(true);
    try {
      const response = await knowledgeAPI.list(selectedAgent);
      setKnowledge(response.data);
    } catch (error) {
      console.error('Failed to fetch knowledge:', error);
    } finally {
      setLoading(false);
    }
  };

  // Close improve dropdowns on outside click
  useEffect(() => {
    if (!showImproveDropdown && !showCreateImproveDropdown) return;
    const handler = (e: MouseEvent) => {
      if (showImproveDropdown && improveDropdownRef.current && !improveDropdownRef.current.contains(e.target as Node)) {
        setShowImproveDropdown(false);
      }
      if (showCreateImproveDropdown && createImproveDropdownRef.current && !createImproveDropdownRef.current.contains(e.target as Node)) {
        setShowCreateImproveDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showImproveDropdown, showCreateImproveDropdown]);

  // Keep editingArticleRef in sync for use in stable event handlers
  useEffect(() => {
    editingArticleRef.current = editingArticle;
  }, [editingArticle]);

  // Reset splitting state when the editor is closed
  useEffect(() => {
    if (!editingArticle) {
      setSplitting(false);
    }
  }, [editingArticle]);

  // Listen for knowledge_split_ready / knowledge_split_failed notifications from SSE stream
  useEffect(() => {
    const handler = (e: Event) => {
      const notif = (e as CustomEvent).detail;
      const currentArticle = editingArticleRef.current;
      if (!currentArticle) return;

      if (notif.type === 'knowledge_split_ready' && notif.metadata?.knowledge_id === currentArticle.id) {
        setSplitting(false);
        const count = notif.metadata?.article_count;
        showToast(
          `Your article was split into ${count} draft article${count !== 1 ? 's' : ''}. Review them in your article list.`,
          'success'
        );
        fetchKnowledge();
      }

      if (notif.type === 'knowledge_split_failed' && notif.metadata?.knowledge_id === currentArticle.id) {
        setSplitting(false);
        showToast('Split failed — please try again', 'error');
      }

      // AI-generated description finished for the article currently open in
      // the editor — refresh the editor's description field so the user sees
      // the new value without re-opening the article. List refetch is handled
      // by the global handler below so a parallel branch isn't needed here.
      if (notif.type === 'knowledge_ai_description_ready' && notif.metadata?.knowledge_id === currentArticle.id) {
        knowledgeAPI.get(currentArticle.id)
          .then((res) => {
            setEditingArticle(res.data);
            setEditDescription(res.data.description ?? '');
          })
          .catch(() => {
            // non-fatal — the global handler refetches the list, so the
            // updated description will appear next time the editor opens.
          });
      }
    };

    const globalHandler = (e: Event) => {
      const notif = (e as CustomEvent).detail;
      if (notif.type === 'help_center_generated') {
        const count = notif.metadata?.article_count ?? '?';
        showToast(`Your Help Center is ready — ${count} articles created`, 'success');
        fetchKnowledge();
      }
      if (notif.type === 'help_center_generation_failed') {
        showToast('Help Center generation failed. Please try again.', 'error');
      }
      // Refresh the article list when an AI description finishes generating
      // so the row's description preview reflects the new value if the user
      // happens to be on the Knowledge page.
      if (notif.type === 'knowledge_ai_description_ready') {
        fetchKnowledge();
      }
    };

    window.addEventListener('app:notification', handler);
    window.addEventListener('app:notification', globalHandler);
    return () => {
      window.removeEventListener('app:notification', handler);
      window.removeEventListener('app:notification', globalHandler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await knowledgeAIAPI.getSuggestions();
      setSuggestions(res.data);
    } catch {
      // non-fatal
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleDismiss = async (conversationId: string) => {
    setDismissingIds(prev => new Set(prev).add(conversationId));
    try {
      await knowledgeAIAPI.dismissSuggestion(conversationId);
    } catch {
      // non-fatal — remove from UI anyway
    }
    setTimeout(() => {
      setSuggestions(prev => prev.filter(s => s.conversation_id !== conversationId));
      setDismissingIds(prev => {
        const next = new Set(prev);
        next.delete(conversationId);
        return next;
      });
    }, 300);
  };

  const handleCreateFromSuggestion = (suggestion: ArticleSuggestion) => {
    setHubTab('articles');
    setManualTitle(suggestion.suggested_title);
    setCreateAIConversationId(suggestion.conversation_id);
    setCreateAIContext('');
    setCreateAITone('friendly');
    setShowCreateAIPanel(true);
    setShowManualForm(true);
    setManualContent('');
  };

  /** Parse accumulated AI text — extract body from JSON or use raw text */
  const parseAIOutput = (raw: string): { content: string; title?: string; collection?: string } => {
    let cleaned = raw.trim();
    // Strip markdown code fences (```json ... ```) that models often wrap around JSON
    const fenceMatch = cleaned.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
    if (fenceMatch) cleaned = fenceMatch[1].trim();
    // Also try extracting the first { ... } block if the above didn't work
    if (!cleaned.startsWith('{')) {
      const braceMatch = cleaned.match(/(\{[\s\S]*\})/);
      if (braceMatch) cleaned = braceMatch[1].trim();
    }
    try {
      const json = JSON.parse(cleaned);
      if (json.body) {
        return { content: json.body, title: json.title, collection: json.suggested_collection };
      }
    } catch {
      // not JSON — use raw
    }
    return { content: raw };
  };

  const handleCreateGenerate = async () => {
    if (!manualTitle.trim()) return;
    setCreateAIGenerating(true);
    setPreviousCreateContent(manualContent);
    setManualContent('');
    createAccumRef.current = '';
    await streamSSE(
      knowledgeAIAPI.draftUrl(),
      {
        title: manualTitle,
        tone: createAITone,
        ...(createAIContext.trim() ? { context: createAIContext } : {}),
        ...(createAIConversationId ? { conversation_id: createAIConversationId } : {}),
      },
      (chunk) => {
        createAccumRef.current += chunk;
      },
      () => {
        setCreateAIGenerating(false);
        const preview = parseAIOutput(createAccumRef.current);
        setCreateAIPreview(preview);
        setManualContent(previousCreateContent);
      },
      (err) => {
        setCreateAIGenerating(false);
        setManualContent(previousCreateContent);
        console.error(err);
        showToast('Generation failed', 'error');
      }
    );
  };

  const handleEditGenerate = async () => {
    if (!editTitle.trim()) return;
    setEditAIGenerating(true);
    setPreviousEditContent(editContent);
    setEditContent('');
    editAccumRef.current = '';
    await streamSSE(
      knowledgeAIAPI.draftUrl(),
      {
        title: editTitle,
        tone: editAITone,
        ...(editAIContext.trim() ? { context: editAIContext } : {}),
      },
      (chunk) => {
        editAccumRef.current += chunk;
      },
      () => {
        setEditAIGenerating(false);
        const preview = parseAIOutput(editAccumRef.current);
        setEditAIPreview(preview);
        setEditContent(previousEditContent);
      },
      (err) => {
        setEditAIGenerating(false);
        setEditContent(previousEditContent);
        console.error(err);
        showToast('Generation failed', 'error');
      }
    );
  };

  const handleImprove = async (improvementType: string) => {
    if (!editingArticle) return;
    setShowImproveDropdown(false);
    setPreviousEditContent(editContent);
    setImproving(true);
    setEditContent('');
    editAccumRef.current = '';
    await streamSSE(
      knowledgeAIAPI.improveUrl(editingArticle.id),
      { improvement_type: improvementType },
      (chunk) => {
        editAccumRef.current += chunk;
      },
      () => {
        setImproving(false);
        const preview = parseAIOutput(editAccumRef.current);
        setEditAIPreview(preview);
        setEditContent(previousEditContent);
      },
      (err) => {
        setImproving(false);
        setEditContent(previousEditContent);
        console.error(err);
        showToast('Improvement failed', 'error');
      }
    );
  };

  const handleCreateImprove = async (improvementType: string) => {
    setShowCreateImproveDropdown(false);
    if (!manualContent.trim()) return;
    const contentSnapshot = manualContent;
    setPreviousCreateContent(contentSnapshot);
    setCreateImproving(true);
    setManualContent('');
    createAccumRef.current = '';
    const labels: Record<string, string> = {
      simplify: 'Simplify the following article',
      expand: 'Expand the following article with more detail',
      fix_tone: 'Fix the tone of the following article to be more professional',
      add_steps: 'Add step-by-step instructions to the following article',
      rewrite: 'Rewrite the following article',
    };
    await streamSSE(
      knowledgeAIAPI.draftUrl(),
      {
        title: manualTitle,
        tone: createAITone,
        context: `${labels[improvementType] || 'Improve the following article'}:\n\n${contentSnapshot}`,
      },
      (chunk) => {
        createAccumRef.current += chunk;
      },
      () => {
        setCreateImproving(false);
        const preview = parseAIOutput(createAccumRef.current);
        setCreateAIPreview(preview);
        setManualContent(previousCreateContent);
      },
      (err) => {
        setCreateImproving(false);
        setManualContent(previousCreateContent);
        console.error(err);
        showToast('Improvement failed', 'error');
      }
    );
  };

  const handleAcceptCreatePreview = () => {
    if (!createAIPreview) return;
    setManualContent(markdownToEditorHtml(createAIPreview.content));
    if (createAIPreview.title) setManualTitle(createAIPreview.title);
    setCreateAIPreview(null);
    showToast('AI content applied!', 'success');
  };

  const handleRejectCreatePreview = () => {
    setManualContent(previousCreateContent);
    setCreateAIPreview(null);
  };

  const handleAcceptEditPreview = () => {
    if (!editAIPreview) return;
    setEditContent(markdownToEditorHtml(editAIPreview.content));
    if (editAIPreview.title) setEditTitle(editAIPreview.title);
    if (editAIPreview.collection) {
      const match = hcCollections.find(c => c.name.toLowerCase() === editAIPreview.collection?.toLowerCase());
      if (match) setEditHCCollectionId(match.id);
    }
    setEditAIPreview(null);
    showToast('AI content applied!', 'success');
  };

  const handleRejectEditPreview = () => {
    setEditContent(previousEditContent);
    setEditAIPreview(null);
  };

  const handleSplit = async () => {
    if (!editingArticle || isEditorHtmlEmpty(editContent)) return;
    setSplitting(true);
    try {
      await knowledgeAIAPI.split(editingArticle.id, editContent);
      showToast("Splitting your article in the background. We'll notify you when it's ready.", 'success');
    } catch {
      showToast('Split failed. Please try again.', 'error');
      setSplitting(false);
    }
  };

  const handleAutoSEO = async () => {
    if (!editingArticle || isEditorHtmlEmpty(editContent)) return;
    setEditSeoGenerating(true);
    try {
      const res = await knowledgeAIAPI.seo(editingArticle.id, { body: editContent });
      setEditHCMetaTitle(res.data.meta_title ?? '');
      setEditHCMetaDesc((res.data.meta_description ?? '').slice(0, 160));
      setEditSeoExpanded(true);
      showToast('SEO fields updated!', 'success');
    } catch {
      showToast('Failed to generate SEO', 'error');
    } finally {
      setEditSeoGenerating(false);
    }
  };

  /** Plan-ShopifyDefaultArticles.md §5.3 (pivoted 2026-06-15) — manual
   *  "Import Shopify Articles" targets the Knowledge Hub dropdown's
   *  currently-selected agent. Overwrites edits + restores deleted seeded
   *  articles on the TARGET agent only. On success: toasts with the imported
   *  count, surfaces cap-exceeded banner if the merchant has > 20 pages, and
   *  reloads the article list. */
  const handleImportShopifyArticles = async () => {
    if (!selectedAgent) return;
    setImporting(true);
    try {
      const res = await shopifyAPI.importContent(selectedAgent);
      const total = (res.data.policies_synced ?? 0) + (res.data.pages_synced ?? 0);
      showToast(`Imported ${total} article${total === 1 ? '' : 's'} from Shopify.`, 'success');
      if (res.data.pages_capped) {
        setPagesCappedActive(true);
        // Reset dismissal so the banner re-surfaces after a fresh cap event.
        if (typeof window !== 'undefined' && capDismissKey) {
          try {
            window.localStorage.removeItem(capDismissKey);
          } catch { /* non-fatal */ }
        }
        setCapDismissed(false);
      }
      setShowImportConfirm(false);
      await fetchKnowledge();
    } catch (error: any) {
      console.error('Failed to import Shopify articles:', error);
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to import Shopify articles';
      showToast(msg, 'error');
    } finally {
      setImporting(false);
    }
  };

  const dismissPagesCappedBanner = () => {
    setCapDismissed(true);
    if (typeof window !== 'undefined' && capDismissKey) {
      try {
        window.localStorage.setItem(capDismissKey, '1');
      } catch { /* non-fatal */ }
    }
  };

  const relativeTime = (isoString: string | null | undefined): string => {
    const ms = serverIsoToMillis(isoString);
    if (ms === null) return NO_DATE;
    const diffMs = Date.now() - ms;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 2) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const fetchCollectionsForEdit = async () => {
    setHCCollectionsLoading(true);
    try {
      const res = await helpCenterAPI.getCollections();
      setHCCollections(res.data);
    } catch {
      // non-fatal
    } finally {
      setHCCollectionsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedAgent) return;
    setPendingUploadFiles(files);
    setFileMetadataList(files.map(f => ({
      title: f.name.replace(/\.[^/.]+$/, ''), // strip extension as default title
      description: '',
      // Follow-up B — Storm flipped 5 backend defaults to 'draft' to
      // match the create-article flow (Bug 1, prior ship). This FE
      // upload-modal default was missed and was still seeding 'published'
      // per file. Flipped to match; user can still click "Published" on
      // each row before submit if they want immediate publication.
      status: 'draft' as const,
      skip_summary: false,
      // Plan §B.4 LOCKED: default OFF. UI only surfaces the toggle for video
      // files (per-file row gates render on `isVideoFile(file)`), so this
      // default is a no-op for the common PDF/DOCX/etc. case.
      analyze_video: false,
    })));
    setShowUploadModal(true);
    e.target.value = '';
  };

  const handleUploadSubmit = async () => {
    if (!pendingUploadFiles.length || !selectedAgent) return;
    setSubmittingUpload(true);
    try {
      const uploadRes = await knowledgeAPI.uploadFiles(
        selectedAgent,
        pendingUploadFiles,
        fileMetadataList,
      );
      // Plan §C.2 — collect (file, source) pairs eligible for analysis. v1
      // limitation surfaced as toast: only the FIRST analyzed video opens in
      // edit-mode pre-filled. Others upload normally; user can edit them
      // manually later. Keeps UX deterministic (one editor open at a time).
      const analyzeTargets: Array<{ source: KnowledgeSource; index: number }> = [];
      pendingUploadFiles.forEach((file, i) => {
        if (!fileMetadataList[i]?.analyze_video) return;
        if (!isVideoFile(file)) return;
        // Match upload response by the same array order — backend returns
        // sources in the same order as the multipart `files` field per the
        // existing knowledge.upload contract.
        const source = uploadRes.data?.[i];
        if (source) analyzeTargets.push({ source, index: i });
      });

      setShowUploadModal(false);
      setPendingUploadFiles([]);
      setFileMetadataList([]);
      // 202 Accepted — backend is processing in background, refresh list to show pending cards
      fetchKnowledge();
      const n = uploadRes.data?.length ?? pendingUploadFiles.length;
      const skipped = fileMetadataList.filter(m => m.skip_summary).length;
      if (skipped === n) {
        showToast(`${n} file${n > 1 ? 's' : ''} uploaded successfully.`, 'success');
      } else {
        showToast(`${n} file${n > 1 ? 's' : ''} uploaded. AI summary generating in background.`, 'success');
      }

      if (analyzeTargets.length > 0) {
        if (analyzeTargets.length > 1) {
          showToast(
            `Analyzing the first video. Re-edit the others to analyze them too (v1 limitation).`,
            'info',
          );
        }
        // Fire and forget — handleAnalyzeUploadedVideo manages its own
        // loading overlay + toasts. Don't await: we don't want to keep the
        // submit button spinner up for the ~30s analysis.
        void handleAnalyzeUploadedVideo(analyzeTargets[0].source);
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
      showToast('Failed to upload files', 'error');
    } finally {
      setSubmittingUpload(false);
    }
  };

  /** Toast wrapper around the shared `formatAnalyzeError` formatter — keeps
   *  the call sites tidy. Per Plan §C.2 error UX, never blocks the underlying
   *  save; the source was already created during the upload step. */
  const toastAnalyzeError = (err: unknown) => {
    showToast(formatAnalyzeError(err, billing?.plan), 'error');
  };

  /** Open an article in edit mode and pre-populate `editContent` with the
   *  Plan §B.7 LOCKED layout (video block, hr-trio, analyzed HTML, hr-trio).
   *  Reuses the existing `handleEdit()` setup work — modal renders identically
   *  to a normal "Edit article" click, the only difference is the seeded HTML. */
  const openArticleInEditWithAnalyzedHtml = async (
    source: KnowledgeSource,
    analyzedHtml: string,
    videoBlock: string,
  ) => {
    try {
      // Run the same edit-modal-init work as handleEdit so all the side state
      // (description, HC fields, collections list) is right.
      const response = await knowledgeAPI.get(source.id);
      const data = response.data;
      editPendingMediaKeysRef.current.clear();
      setEditingArticle(data);
      setEditTitle(data.title);
      setEditContent(buildAnalyzedArticleHtml(videoBlock, analyzedHtml));
      setEditStatus(data.status ?? 'published');
      setEditDescription(data.description ?? '');
      setEditAiGenerateDescription(false);
      setEditHCSlug(data.help_center_slug ?? '');
      setEditHCMetaTitle(data.meta_title ?? '');
      setEditHCMetaDesc(data.meta_description ?? '');
      setEditSeoExpanded(false);
      await fetchCollectionsForEdit();
      setEditHCCollectionId(data.collection_id ?? '');
    } catch (err) {
      console.error('Failed to open analyzed article in edit mode', err);
      showToast('Analysis succeeded but the editor failed to open. Open the article manually.', 'error');
    }
  };

  /** Run the analyze-video flow for a freshly-uploaded video source. Uses
   *  the source's S3 key (resolved via `s3_file_url`) and opens the article
   *  in edit mode on success. */
  const handleAnalyzeUploadedVideo = async (source: KnowledgeSource) => {
    if (!selectedAgent) return;
    // Source-shape sanity — extract an S3 KEY (not the full URL) per the
    // backend contract. The metadata on KnowledgeSource carries the FULL S3
    // URL (`s3_file_url`); we strip the bucket+host so backend can re-upload
    // through its own boto3 streaming pipeline. Reuses the existing
    // `extractS3KeyFromUrl` defined further down in this component (the
    // image-orphan-cleanup helper) — same canonical S3 URL parser.
    if (!source.s3_file_url) {
      showToast(
        'Analysis is unavailable for this video — no S3 URL on the uploaded article.',
        'error',
      );
      return;
    }
    const s3Key = extractS3KeyFromUrl(source.s3_file_url);
    if (!s3Key) {
      showToast(
        'Analysis is unavailable for this video — could not parse S3 key from URL.',
        'error',
      );
      return;
    }
    setAnalyzingVideo(true);
    try {
      const res = await knowledgeAIAPI.analyzeVideo({
        video_s3_key: s3Key,
        agent_id: selectedAgent,
        format: 'structured_html',
      });
      const videoBlock = uploadedVideoEmbedHtml(source.s3_file_url || '');
      await openArticleInEditWithAnalyzedHtml(source, res.data.content_html, videoBlock);
    } catch (err) {
      toastAnalyzeError(err);
    } finally {
      setAnalyzingVideo(false);
    }
  };

  /** Run the analyze-video flow for a YouTube URL that was pasted into the
   *  scrape textarea. The matching scraped article is found by URL match;
   *  if none is found the analyzed content is rendered into a brand-new
   *  manual article instead — keeps the user from losing their analysis on
   *  a transient scrape failure. */
  const handleAnalyzeYouTubeUrl = async (
    youtubeUrl: string,
    scrapedSources: KnowledgeSource[] | undefined,
  ) => {
    if (!selectedAgent) return;
    setAnalyzingVideo(true);
    try {
      const res = await knowledgeAIAPI.analyzeVideo({
        video_url: youtubeUrl,
        agent_id: selectedAgent,
        format: 'structured_html',
      });
      const videoBlock = youtubeEmbedHtml(youtubeUrl);
      // Best-effort find the scraped article matching this URL. Falls through
      // to a fresh manual create if nothing matches (e.g., scrape couldn't
      // resolve the YouTube watch page or stripped tracking params).
      const matched = (scrapedSources || []).find(s => {
        const u = s.source_url || '';
        return u.trim() === youtubeUrl.trim();
      });
      if (matched) {
        await openArticleInEditWithAnalyzedHtml(matched, res.data.content_html, videoBlock);
      } else {
        // Fallback — create a fresh manual article with the analyzed content
        // already in body, so the user doesn't lose their analysis.
        const fallbackTitle = `Video analysis — ${youtubeUrl.slice(0, 60)}`;
        const created = await knowledgeAPI.createManual(
          selectedAgent,
          fallbackTitle,
          buildAnalyzedArticleHtml(videoBlock, res.data.content_html),
          'draft',
          '',
          false,
        );
        fetchKnowledge();
        await openArticleInEditWithAnalyzedHtml(created.data, res.data.content_html, videoBlock);
      }
    } catch (err) {
      toastAnalyzeError(err);
    } finally {
      setAnalyzingVideo(false);
    }
  };

  const updateFileMeta = (
    index: number,
    field: 'title' | 'description' | 'status' | 'skip_summary' | 'analyze_video',
    value: string | boolean,
  ) => {
    setFileMetadataList(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const handleScrapeUrls = async () => {
    if (!urls.trim() || !selectedAgent) return;

    const urlList = urls.split('\n').map(u => u.trim()).filter(u => u);
    // Pluck the FIRST YouTube URL out of the list iff the user opted into
    // analysis — Plan §C.2 v1 only analyzes one video per submit (keeps the
    // editor open for one article at a time). The URL is still scraped via
    // the normal flow so the source row exists and can be linked from the
    // analyzed article. Subsequent YouTube URLs scrape but don't analyze.
    const firstYouTube = urlAnalyzeVideo
      ? urlList.find(u => isYouTubeUrl(u))
      : null;
    setScrapingUrls(true);
    try {
      const scrapeRes = await knowledgeAPI.scrapeUrls(selectedAgent, urlList);
      await fetchKnowledge();
      setUrls('');
      showToast('URLs scraped successfully!', 'success');

      if (firstYouTube) {
        const youtubeCount = urlList.filter(u => isYouTubeUrl(u)).length;
        if (youtubeCount > 1) {
          showToast(
            `Analyzing the first YouTube URL. The other ${youtubeCount - 1} were scraped but not analyzed (v1 limitation).`,
            'info',
          );
        }
        // Reset the toggle so the next paste batch starts at a clean slate.
        setUrlAnalyzeVideo(false);
        // Fire and forget — internal overlay handles the ~30s wait.
        void handleAnalyzeYouTubeUrl(firstYouTube, scrapeRes.data);
      }
    } catch (error) {
      console.error('Failed to scrape URLs:', error);
      showToast('Failed to scrape URLs', 'error');
    } finally {
      setScrapingUrls(false);
    }
  };

  // Bug 1 — accepts an OPTIONAL `statusOverride` so the two action
  // buttons in the create form can pass the desired status DIRECTLY
  // instead of relying on `setManualStatus(...)` immediately followed
  // by `handleCreateManual()` (which previously read stale closure
  // state and POSTed the prior `manualStatus` value, defaulting to
  // 'published'). The override wins; if absent, the current state
  // value is used (legacy behavior).
  const handleCreateManual = async (statusOverride?: 'draft' | 'published') => {
    if (!selectedAgent) return;
    if (!manualTitle.trim()) {
      showToast('Article title is required', 'error');
      return;
    }
    if (isEditorHtmlEmpty(manualContent)) {
      showToast('Article content is required', 'error');
      return;
    }

    const effectiveStatus = statusOverride ?? manualStatus;
    try {
      setManualSaving(true);
      const contentWithS3 = await uploadPendingImages(manualContent);
      const wantedAiDesc = manualAiGenerateDescription;
      const res = await knowledgeAPI.createManual(
        selectedAgent,
        manualTitle,
        contentWithS3,
        effectiveStatus,
        manualAiGenerateDescription ? '' : manualDescription,
        manualAiGenerateDescription,
      );
      // Delete any media the user uploaded then removed before saving.
      cleanupOrphanedMedia(createPendingMediaKeysRef, contentWithS3);
      fetchKnowledge();
      setManualTitle('');
      setManualContent('');
      setManualDescription('');
      setManualAiGenerateDescription(false);
      setManualStatus('draft');
      setShowManualForm(false);
      clearPendingImages();
      if (wantedAiDesc && !(res.data?.description ?? '').trim()) {
        showToast('Article saved, but AI description was unavailable. You can edit to add one.', 'info');
      } else {
        showToast(
          effectiveStatus === 'draft' ? 'Draft saved!' : 'Article published!',
          'success',
        );
      }
    } catch (error: any) {
      console.error('Failed to create article:', error);
      const detail = error?.response?.data?.detail;
      // Bug 3 — if Storm's BE 400 contract for empty-content + AI-generate
      // surfaces a structured `{code, message}` detail, prefer message;
      // otherwise fall back to a string detail or generic.
      const detailMsg = (detail && typeof detail === 'object' && typeof detail.message === 'string')
        ? detail.message
        : (typeof detail === 'string' ? detail : 'Failed to create article');
      showToast(detailMsg, 'error');
    } finally {
      // Always clear the flag — success closes the form, error keeps it open
      // and the user can retry. Either path needs the button re-enabled.
      setManualSaving(false);
    }
  };

  const handleToggleHelpCenter = async (ks: KnowledgeSource) => {
    const newVal = !ks.help_center_enabled;
    // Optimistic update
    setKnowledge(prev => prev.map(k => k.id === ks.id ? { ...k, help_center_enabled: newVal } : k));
    try {
      await knowledgeAPI.toggleHelpCenter(ks.id, newVal);
    } catch {
      // Revert on failure
      setKnowledge(prev => prev.map(k => k.id === ks.id ? { ...k, help_center_enabled: ks.help_center_enabled } : k));
      showToast('Failed to update Help Center status', 'error');
    }
  };

  const handleDeleteClick = (id: string, title: string) => {
    setArticleToDelete({ id, title });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!articleToDelete) return;
    try {
      await knowledgeAPI.delete(articleToDelete.id);
      fetchKnowledge();
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setArticleToDelete(null);
    }
  };

  const handleTransferClick = (id: string, title: string) => {
    setTransferArticle({ id, title });
    setTransferAction('copy');
    setTransferTargetAgent('');
  };

  const handleTransferConfirm = async () => {
    if (!transferArticle || !transferTargetAgent) return;
    const targetAgent = agents.find(a => a.agent_id === transferTargetAgent);
    const targetName = targetAgent?.config.agent_name || 'agent';
    setTransferLoading(true);
    try {
      await knowledgeAPI.transfer(transferArticle.id, transferTargetAgent, transferAction);
      showToast(
        transferAction === 'move'
          ? `Article moved to ${targetName}`
          : `Article copied to ${targetName}`,
        'success'
      );
      setTransferArticle(null);
      fetchKnowledge();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'Transfer failed — please try again';
      showToast(typeof msg === 'string' ? msg : 'Transfer failed', 'error');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleBulkTransferClick = () => {
    if (!selectedIds.size) return;
    setBulkTransferAction('copy');
    setBulkTransferTargetAgent('');
    setBulkTransferOpen(true);
  };

  const handleBulkTransferConfirm = async () => {
    if (!bulkTransferTargetAgent || !selectedIds.size) return;
    const targetAgent = agents.find(a => a.agent_id === bulkTransferTargetAgent);
    const targetName = targetAgent?.config.agent_name || 'agent';
    const ids = Array.from(selectedIds);
    setBulkTransferLoading(true);
    try {
      const res = await knowledgeAPI.bulkTransfer(ids, bulkTransferTargetAgent, bulkTransferAction);
      const { moved = 0, copied = 0, failed = 0 } = res.data || ({} as any);
      const successCount = bulkTransferAction === 'move' ? moved : copied;
      const verb = bulkTransferAction === 'move' ? 'moved' : 'copied';
      if (failed > 0) {
        showToast(
          `${successCount} article${successCount !== 1 ? 's' : ''} ${verb} to ${targetName}, ${failed} failed`,
          successCount > 0 ? 'success' : 'error'
        );
      } else {
        showToast(
          `${successCount} article${successCount !== 1 ? 's' : ''} ${verb} to ${targetName}`,
          'success'
        );
      }
      setBulkTransferOpen(false);
      setSelectedIds(new Set());
      fetchKnowledge();
    } catch (error: any) {
      const msg = error?.response?.data?.detail || 'Bulk transfer failed — please try again';
      showToast(typeof msg === 'string' ? msg : 'Bulk transfer failed', 'error');
    } finally {
      setBulkTransferLoading(false);
    }
  };

  const handleView = async (article: KnowledgeSource) => {
    try {
      const response = await knowledgeAPI.get(article.id);
      setViewingArticle(response.data);
      // Fetch file preview if article has a preview_type
      if (response.data.preview_type) {
        try {
          const previewRes = await knowledgeAPI.getPreview(article.id);
          setViewingPreview(previewRes.data);
        } catch {
          setViewingPreview(null);
        }
      } else {
        setViewingPreview(null);
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      showToast('Failed to load article', 'error');
    }
  };

  const handleEdit = async (article: KnowledgeSource) => {
    try {
      const response = await knowledgeAPI.get(article.id);
      const data = response.data;
      editPendingMediaKeysRef.current.clear();
      setEditingArticle(data);
      setEditTitle(data.title);
      // Phase 1 WYSIWYG: convert legacy markdown → HTML on load. HTML articles pass through.
      // Then one-time inject any sidecar videos that aren't already inline, so staff can
      // drag them into position. Dedup prevents re-append after save/reopen.
      const html = markdownToEditorHtml(data.content || '');
      setEditContent(injectMissingSidecarVideos(html, {
        media_videos: data.media_videos,
        video_urls: data.video_urls,
      }));
      setEditStatus(data.status ?? 'published');
      setEditDescription(data.description ?? '');
      setEditAiGenerateDescription(false);
      // Init HC meta fields
      setEditHCSlug(data.help_center_slug ?? '');
      setEditHCMetaTitle(data.meta_title ?? '');
      setEditHCMetaDesc(data.meta_description ?? '');
      setEditSeoExpanded(false);
      await fetchCollectionsForEdit();
      setEditHCCollectionId(data.collection_id ?? '');
    } catch (error) {
      console.error('Failed to fetch article:', error);
      showToast('Failed to load article for editing', 'error');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingArticle) return;
    if (!editTitle.trim()) {
      showToast('Article title is required', 'error');
      return;
    }
    if (isEditorHtmlEmpty(editContent)) {
      showToast('Article content is required', 'error');
      return;
    }

    try {
      setEditSaving(true);
      const contentWithS3 = await uploadPendingImages(editContent);
      await knowledgeAPI.update(
        editingArticle.id,
        editTitle,
        contentWithS3,
        editStatus,
        editAiGenerateDescription ? '' : editDescription,
        editAiGenerateDescription,
      );
      cleanupOrphanedMedia(editPendingMediaKeysRef, contentWithS3);
      // Save HC meta separately
      await knowledgeAPI.updateHelpCenterMeta(editingArticle.id, {
        enabled: editingArticle.help_center_enabled ?? false,
        meta_title: editHCMetaTitle || undefined,
        meta_description: editHCMetaDesc || undefined,
        // Strict — the user may hit Save without blurring the field, so a
        // mid-typing "my-article-" must not be what gets persisted.
        help_center_slug: slugifyStrict(editHCSlug) || undefined,
        collection_id: editHCCollectionId || null,
      });
      setEditingArticle(null);
      clearPendingImages();
      fetchKnowledge();
      showToast('Article updated successfully!', 'success');
    } catch (error) {
      console.error('Failed to update:', error);
      showToast('Failed to update article', 'error');
    } finally {
      // Always clear the flag — success closes the modal, error keeps it open.
      // Either path needs the button re-enabled.
      setEditSaving(false);
    }
  };

  // Uploads all pending blob-URL images in content to S3, returns updated content with S3 URLs
  const uploadPendingImages = async (content: string): Promise<string> => {
    if (pendingImagesRef.current.size === 0) return content;
    let updated = content;
    for (const [blobUrl, file] of Array.from(pendingImagesRef.current.entries())) {
      if (!updated.includes(blobUrl)) {
        URL.revokeObjectURL(blobUrl);
        pendingImagesRef.current.delete(blobUrl);
        continue;
      }
      const response = await knowledgeAPI.uploadImage(selectedAgent, file);
      const s3Url = response.data.url.replace(/ /g, '%20');
      updated = updated.split(blobUrl).join(s3Url);
      URL.revokeObjectURL(blobUrl);
      pendingImagesRef.current.delete(blobUrl);
    }
    return updated;
  };

  const clearPendingImages = () => {
    pendingImagesRef.current.forEach((_, blobUrl) => URL.revokeObjectURL(blobUrl));
    pendingImagesRef.current.clear();
  };

  /** Extract the S3 key from an S3 URL. Returns null if not an S3 URL. */
  const extractS3KeyFromUrl = (url: string): string | null => {
    try {
      const u = new URL(url);
      if (!u.hostname.includes('.s3.') && !u.hostname.startsWith('s3.')) return null;
      return decodeURIComponent(u.pathname.replace(/^\//, ''));
    } catch {
      return null;
    }
  };

  /** Collect every S3 key referenced by <img>/<video>/<source> tags in an HTML string. */
  const collectMediaKeysFromHtml = (html: string): Set<string> => {
    const keys = new Set<string>();
    if (!html) return keys;
    const re = /(?:src|href)\s*=\s*["']([^"']+)["']/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(html)) !== null) {
      const k = extractS3KeyFromUrl(m[1]);
      if (k) keys.add(k);
    }
    return keys;
  };

  /**
   * Fire-and-forget cleanup of orphaned pending media.
   * - On cancel: pass no `finalHtml` → all tracked keys are orphaned and deleted.
   * - On save: pass the saved HTML → keys not referenced by the final HTML are orphaned and deleted.
   * Always clears the ref when done.
   */
  const cleanupOrphanedMedia = (
    ref: React.MutableRefObject<Set<string>>,
    finalHtml?: string
  ) => {
    const pending = Array.from(ref.current);
    ref.current.clear();
    if (!pending.length) return;
    let orphans: string[];
    if (finalHtml === undefined) {
      orphans = pending;
    } else {
      const kept = collectMediaKeysFromHtml(finalHtml);
      orphans = pending.filter((k) => !kept.has(k));
    }
    if (!orphans.length) return;
    knowledgeAPI.cleanupPendingMedia(orphans).catch(() => {});
  };

  const handleBulkAction = async (action: 'publish' | 'draft' | 'delete') => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    try {
      const res = await knowledgeAPI.bulkAction(Array.from(selectedIds), action);
      showToast(`${res.data.affected} article${res.data.affected !== 1 ? 's' : ''} ${action === 'delete' ? 'deleted' : action === 'publish' ? 'published' : 'set to draft'}`, 'success');
      setSelectedIds(new Set());
      fetchKnowledge();
    } catch {
      showToast(`Failed to ${action} articles`, 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkHelpCenter = async () => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    try {
      const res = await knowledgeAPI.bulkHelpCenter(Array.from(selectedIds), true);
      showToast(`${res.data.affected} article${res.data.affected !== 1 ? 's' : ''} added to Help Center`, 'success');
      setSelectedIds(new Set());
      fetchKnowledge();
    } catch {
      showToast('Failed to add articles to Help Center', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkCollection = async (collectionId: string | null) => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    try {
      const res = await knowledgeAPI.bulkCollection(Array.from(selectedIds), collectionId);
      showToast(`${res.data.affected} article${res.data.affected !== 1 ? 's' : ''} ${collectionId ? 'added to collection' : 'removed from collection'}`, 'success');
      setSelectedIds(new Set());
      setShowBulkCollectionPicker(false);
      fetchKnowledge();
    } catch {
      showToast('Failed to add articles to collection', 'error');
    } finally {
      setBulkLoading(false);
    }
  };

  const openBulkCollectionPicker = async () => {
    setShowBulkCollectionPicker(true);
    try {
      const res = await helpCenterAPI.getCollections();
      setBulkCollections(res.data);
    } catch {
      showToast('Failed to load collections', 'error');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === knowledge.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(knowledge.map(k => k.id)));
    }
  };

  // Get source type icon
  const getSourceIcon = (ks: KnowledgeSource) => {
    if (ks.source_type === 'image' || ks.has_images) {
      return <Image className="w-5 h-5 text-white" />;
    }
    if (ks.source_type === 'video' || ks.has_video) {
      return <Video className="w-5 h-5 text-white" />;
    }
    return <FileText className="w-5 h-5 text-white" />;
  };

  // Get icon gradient based on type
  const getIconGradient = (ks: KnowledgeSource) => {
    if (ks.source_type === 'image' || ks.has_images) {
      return 'from-green-500 to-emerald-600';
    }
    if (ks.source_type === 'video' || ks.has_video) {
      return 'from-blue-500 to-indigo-600';
    }
    return 'from-primary to-purple-600';
  };

  // Shared ReactMarkdown component map used in Write/Preview tabs
  const previewComponents: React.ComponentProps<typeof ReactMarkdown>['components'] = {
    img: ({ src, alt }) => (
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img src={src} alt={alt || ''} className="max-w-full h-auto rounded-lg my-2 cursor-pointer" />
      </a>
    ),
    // ReactMarkdown parses [VIDEO: title](url) as a standard <a> node, so the fix lives here.
    a: ({ href, children, ...props }) => {
      const url = href || '';
      const linkText = String(children ?? '');
      const isVideoToken = linkText.startsWith('VIDEO:');

      let isYouTube = false;
      let embedUrl = '';
      try {
        if (url.includes('youtube.com/watch') || url.includes('youtu.be/')) {
          isYouTube = true;
          const videoId = url.includes('youtube.com/watch')
            ? new URLSearchParams(new URL(url).search).get('v')
            : url.split('youtu.be/')[1]?.split('?')[0];
          if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0`;
        }
      } catch (_) {}

      if (isVideoToken || isYouTube) {
        if (isYouTube && embedUrl) {
          return (
            <div className="mt-2 mb-2 w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-black aspect-video relative">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={embedUrl}
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }
        return (
          <video controls playsInline preload="metadata" className="max-w-full rounded-lg my-2">
            <source src={withPosterFrame(url)} />
          </video>
        );
      }

      return <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 underline" {...props}>{children}</a>;
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-4xl font-bold gradient-text">Knowledge Hub</h1>
        <div className="flex items-center flex-wrap gap-2">
          {billing?.shopify_offline_token_present && selectedAgent && (
            <button
              onClick={() => setShowImportConfirm(true)}
              data-tour-target="import-shopify-articles"
              className="btn-secondary flex items-center space-x-2"
              title="Import policies and pages from your Shopify store into the selected agent"
            >
              <Store className="w-5 h-5" />
              <span>Import Shopify Articles</span>
            </button>
          )}
          <button
            onClick={() => setShowGuide(!showGuide)}
            className="btn-secondary flex items-center space-x-2"
          >
            <HelpCircle className="w-5 h-5" />
            <span>Guide</span>
          </button>
          <button
            onClick={() => { setShowManualForm(!showManualForm); }}
            data-tour-target="knowledge-add-article"
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Create Article</span>
          </button>
        </div>
      </div>

      {/* Shopify cap-exceeded banner — surfaces when last Import hit the 20-page cap.
          Dismiss state persists per workspace via localStorage. Gate on token presence
          (workspace has an install) rather than the pre-pivot shopify_agent_id pointer. */}
      {billing?.shopify_offline_token_present && pagesCappedActive && !capDismissed && (
        <div className="flex items-start gap-3 p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-sm">
          <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="flex-1">
            Your store has more than 20 pages. We seeded your 20 most-recently-updated
            pages. You can add the rest as articles manually.
          </span>
          <button
            onClick={dismissPagesCappedBanner}
            className="flex-shrink-0 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 text-xs font-medium underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Import Shopify Articles — confirmation modal */}
      {showImportConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Store className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span>Import Shopify Articles</span>
                </h2>
              </div>
              <button
                onClick={() => !importing && setShowImportConfirm(false)}
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                disabled={importing}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This will import your Shopify store's policies and static pages into{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {currentAgent?.config?.agent_name || 'this agent'}
                </span>
                's knowledge base. If imported before, your edits to seeded articles will be
                overwritten with the current Shopify content. Pages are capped at 20 (sorted by
                most-recently-updated).
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-white pt-1">Continue?</p>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setShowImportConfirm(false)}
                className="btn-secondary w-full sm:w-auto"
                disabled={importing}
              >
                Cancel
              </button>
              <button
                onClick={handleImportShopifyArticles}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                disabled={importing}
              >
                {importing && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{importing ? 'Importing…' : 'Import now'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guide Section */}
      {showGuide && (
        <div className="card-hover border-l-4 border-primary">
          <div className="flex items-start justify-between mb-3">
            <h2 className="text-xl font-bold gradient-text flex items-center space-x-2">
              <HelpCircle className="w-5 h-5" />
              <span>Knowledge Hub Guide</span>
            </h2>
            <button onClick={() => setShowGuide(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-gray-400">
              The Knowledge Hub is where you manage all the information your AI agent uses to answer customer questions. Here's how to use each feature:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span>Create Article</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Write articles with Markdown formatting. Supports bold, italics, lists, headers, and more.</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <Image className="w-4 h-4 text-green-600" />
                  <span>Images</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Upload images or paste from clipboard. Insert image URLs directly. Uses Markdown syntax: <code>![alt](url)</code></p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <Video className="w-4 h-4 text-blue-600" />
                  <span>Videos</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Upload video files or link video URLs. Uses token syntax: <code>[VIDEO: title](url)</code></p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <Upload className="w-4 h-4 text-purple-600" />
                  <span>File Upload & Scraping</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Upload PDF, DOCX, and other docs or scrape website URLs to auto-extract content.</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <Layers className="w-4 h-4 text-indigo-600" />
                  <span>Collections</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Collections are folders to organize your articles. You can choose which collections appear on your Help Center from the Help Center Studio settings.</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-1">
                  <Info className="w-4 h-4 text-amber-600" />
                  <span>Status: Draft vs Published</span>
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Draft articles are not used by AI Agents and not shown on the Help Center. Only Published articles are active — used by AI Agents to answer customer questions and displayed on your public Help Center.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move / Copy Article Modal */}
      {transferArticle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                  <span>Move or Copy Article</span>
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 truncate" title={transferArticle.title}>
                  {transferArticle.title}
                </p>
              </div>
              <button
                onClick={() => !transferLoading && setTransferArticle(null)}
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                disabled={transferLoading}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {/* Action radio group */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                      transferAction === 'copy'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="transferAction"
                      value="copy"
                      checked={transferAction === 'copy'}
                      onChange={() => setTransferAction('copy')}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900 dark:text-white">Copy</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      A duplicate will be created under the selected agent.
                    </div>
                  </label>
                  <label
                    className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                      transferAction === 'move'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="transferAction"
                      value="move"
                      checked={transferAction === 'move'}
                      onChange={() => setTransferAction('move')}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900 dark:text-white">Move</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      The article will be transferred and no longer appear under the current agent.
                    </div>
                  </label>
                </div>
              </div>

              {/* Target agent dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target AI Agent
                </label>
                <select
                  value={transferTargetAgent}
                  onChange={(e) => setTransferTargetAgent(e.target.value)}
                  className="input-field w-full"
                  disabled={transferLoading}
                >
                  <option value="">Select an agent…</option>
                  {agents
                    .filter(a => a.agent_id !== selectedAgent)
                    .map(a => (
                      <option key={a.agent_id} value={a.agent_id}>
                        {a.config.agent_name || a.agent_id}
                      </option>
                    ))}
                </select>
                {agents.filter(a => a.agent_id !== selectedAgent).length === 0 && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    No other agents available in this workspace.
                  </p>
                )}
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setTransferArticle(null)}
                className="btn-secondary w-full sm:w-auto"
                disabled={transferLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferConfirm}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                disabled={!transferTargetAgent || transferLoading}
              >
                {transferLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>{transferAction === 'move' ? 'Move Article' : 'Copy Article'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Move / Copy Modal */}
      {bulkTransferOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                  <span>Move or Copy {selectedIds.size} Article{selectedIds.size !== 1 ? 's' : ''}</span>
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Transfer the selected articles to another AI agent.
                </p>
              </div>
              <button
                onClick={() => !bulkTransferLoading && setBulkTransferOpen(false)}
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                disabled={bulkTransferLoading}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Action</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label
                    className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                      bulkTransferAction === 'copy'
                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bulkTransferAction"
                      value="copy"
                      checked={bulkTransferAction === 'copy'}
                      onChange={() => setBulkTransferAction('copy')}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900 dark:text-white">Copy</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Duplicates will be created under the selected agent.
                    </div>
                  </label>
                  <label
                    className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-colors ${
                      bulkTransferAction === 'move'
                        ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bulkTransferAction"
                      value="move"
                      checked={bulkTransferAction === 'move'}
                      onChange={() => setBulkTransferAction('move')}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900 dark:text-white">Move</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Articles will be transferred and no longer appear under the current agent.
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Target AI Agent
                </label>
                <select
                  value={bulkTransferTargetAgent}
                  onChange={(e) => setBulkTransferTargetAgent(e.target.value)}
                  className="input-field w-full"
                  disabled={bulkTransferLoading}
                >
                  <option value="">Select an agent…</option>
                  {agents
                    .filter(a => a.agent_id !== selectedAgent)
                    .map(a => (
                      <option key={a.agent_id} value={a.agent_id}>
                        {a.config.agent_name || a.agent_id}
                      </option>
                    ))}
                </select>
                {agents.filter(a => a.agent_id !== selectedAgent).length === 0 && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    No other agents available in this workspace.
                  </p>
                )}
              </div>
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                onClick={() => setBulkTransferOpen(false)}
                className="btn-secondary w-full sm:w-auto"
                disabled={bulkTransferLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkTransferConfirm}
                className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2"
                disabled={!bulkTransferTargetAgent || bulkTransferLoading}
              >
                {bulkTransferLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                <span>
                  {bulkTransferAction === 'move' ? 'Move' : 'Copy'} {selectedIds.size} Article{selectedIds.size !== 1 ? 's' : ''}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Knowledge Article"
        message={`Are you sure you want to delete "${articleToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />

      {/* Agent Selector */}
      <div className="card">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Agent
        </label>
        <select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          className="input-field"
        >
          {agents.map((agent) => (
            <option key={agent.id} value={agent.agent_id}>
              {agent.config.agent_name}
            </option>
          ))}
        </select>
      </div>

      {/* Manual Article Form */}
      {showManualForm && (
        <div className="card-hover">
          <h2 className="text-2xl font-bold gradient-text mb-4 flex items-center space-x-2">
            <BookOpen className="w-6 h-6" />
            <span>Create Knowledge Article</span>
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Article Title *
              </label>
              <input
                type="text"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                placeholder="e.g., Refund Policy, Shipping Information"
                className="input-field"
              />
              <div className="flex justify-end mt-1.5">
                <button
                  type="button"
                  onClick={() => setShowCreateAIPanel(p => !p)}
                  className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Generate with AI
                </button>
              </div>
            </div>
            {/* Generate with AI panel */}
            {showCreateAIPanel && (
              <div className="border border-indigo-200 dark:border-indigo-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/20">
                  <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Generate with AI</span>
                </div>
                <div className="p-4 space-y-3">
                  {createAIConversationId && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-700 dark:text-blue-300">
                      <MessageCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Generating from conversation context
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tone</label>
                    <div className="flex gap-1.5">
                      {(['friendly', 'formal', 'concise'] as const).map(t => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setCreateAITone(t)}
                          className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                            createAITone === t
                              ? 'bg-primary text-white border-primary'
                              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary/50'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                      Context <span className="font-normal text-gray-400">(optional — paste a ticket, describe the issue, or leave blank)</span>
                    </label>
                    <textarea
                      value={createAIContext}
                      onChange={e => setCreateAIContext(e.target.value)}
                      placeholder="Paste a ticket, describe the issue, or leave blank"
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateGenerate}
                    disabled={!manualTitle.trim() || createAIGenerating}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {createAIGenerating ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Generating…</>
                    ) : (
                      <><Sparkles className="w-4 h-4" />Generate</>
                    )}
                  </button>
                </div>
              </div>
            )}
            <div>
              <div className="flex items-start justify-between gap-3 mb-2 flex-col sm:flex-row sm:items-center">
                <label htmlFor="manual-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                {/*
                  Bug 3 — disable the AI-generate-description toggle when
                  article content is empty (the AI has nothing to
                  summarize). Gate ONLY when toggle is currently OFF,
                  so a user who already enabled it then deleted content
                  can still click to turn it back OFF. `title` provides
                  a native hover tooltip explaining the gate.
                */}
                {(() => {
                  const contentEmpty = isEditorHtmlEmpty(manualContent);
                  const gated = contentEmpty && !manualAiGenerateDescription;
                  return (
                    <label
                      className={`flex items-center gap-2 select-none ${gated ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                      title={gated ? 'Add article content first to generate a description.' : undefined}
                    >
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Auto-generate with AI
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={manualAiGenerateDescription}
                        aria-disabled={gated}
                        disabled={gated}
                        onClick={() => setManualAiGenerateDescription(v => !v)}
                        title={gated ? 'Add article content first to generate a description.' : undefined}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed ${
                          manualAiGenerateDescription ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            manualAiGenerateDescription ? 'translate-x-[18px]' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </label>
                  );
                })()}
              </div>
              <textarea
                id="manual-description"
                value={manualAiGenerateDescription ? '' : manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                disabled={manualAiGenerateDescription}
                rows={2}
                placeholder={manualAiGenerateDescription ? '' : 'Briefly describe what this article covers (optional)'}
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none disabled:bg-gray-100 dark:disabled:bg-gray-800/60 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
              />
              {manualAiGenerateDescription && (
                <p className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 flex-shrink-0" />
                  AI will generate this from your content on save.
                </p>
              )}
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                A good description helps our AI answer customer questions faster and more accurately. Keep it concise — one sentence is ideal.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Article Content *
              </label>
              {/* Toolbar row: Improve with AI dropdown */}
              <div className="flex items-center space-x-1 mb-2">
                <div className="relative ml-auto" ref={createImproveDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowCreateImproveDropdown(p => !p)}
                    disabled={createImproving || isEditorHtmlEmpty(manualContent)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {createImproving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    Improve with AI
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showCreateImproveDropdown && (
                    <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                      {[
                        { label: 'Simplify', type: 'simplify' },
                        { label: 'Expand', type: 'expand' },
                        { label: 'Fix Tone', type: 'fix_tone' },
                        { label: 'Add Steps', type: 'add_steps' },
                        { label: 'Rewrite', type: 'rewrite' },
                      ].map(opt => (
                        <button
                          key={opt.type}
                          type="button"
                          onClick={() => handleCreateImprove(opt.type)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {createAIPreview ? (
                <div className="border border-emerald-200 dark:border-emerald-800 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">AI Preview</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleAcceptCreatePreview}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Accept
                      </button>
                      <button
                        type="button"
                        onClick={handleRejectCreatePreview}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                        Reject
                      </button>
                    </div>
                  </div>
                  {createAIPreview.title && (
                    <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-emerald-100 dark:border-emerald-800">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Suggested title: </span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{createAIPreview.title}</span>
                    </div>
                  )}
                  <div className="p-4 max-h-64 overflow-y-auto">
                    <div className="prose dark:prose-invert max-w-none text-sm">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} urlTransform={(url) => url} components={previewComponents}>{createAIPreview.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <RichTextEditor
                    mode="article"
                    agentId={selectedAgent}
                    content={manualContent}
                    onChange={(html) => setManualContent(html)}
                    placeholder="Write your article content..."
                    onMediaUploaded={(key) => createPendingMediaKeysRef.current.add(key)}
                  />
                  {(createAIGenerating || createImproving) && (
                    <div className="absolute inset-0 bg-white/75 dark:bg-gray-800/75 flex items-center justify-center rounded-xl z-10">
                      <div className="flex items-center gap-2 text-primary">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm font-medium">{createImproving ? 'Improving…' : 'Generating…'}</span>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This content will be used by the AI to answer customer questions. What you see is what ships to the Help Center.
                  </p>
                </div>
              )}
              {/* Credit disclaimer. 🚨 Legacy-only: `knowledge_summaries` is in
                  `POOL_RETIRED_CASCADE_RESOURCES`, so under pooled billing there
                  is no "Chat credit" and no per-call unit to quote. Hidden, not
                  reworded — the pool has no per-call price to state.

                  🚨 TWIN — this block is duplicated VERBATIM further down this
                  file, on the Help Center article editor, and THE TWO MUST MOVE
                  TOGETHER (same convention as the paired Orchestrator rows in
                  BillingPage.tsx / ShopifyBillingView.tsx). Gating one and not
                  the other leaves the same lie on the other editor, which is
                  precisely how a sweep misses a site. */}
              {showLegacyCredits && (
                <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-2">
                  <Info className="w-3 h-3 flex-shrink-0" />
                  AI generation uses Chat credits (1 credit per API call)
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/*
                Bug 1 — Save as Draft is now the PRIMARY action (per CEO
                direction; accidental publishes are the bug). Status is
                passed via explicit override arg to avoid the stale-closure
                race that the prior `setManualStatus(...); handleCreateManual()`
                pair created (state update was queued; the call read the
                pre-update value; both buttons effectively POSTed whatever
                was already in state — usually the published default).
              */}
              <button
                onClick={() => { setManualStatus('draft'); handleCreateManual('draft'); }}
                disabled={manualSaving || !manualTitle.trim() || isEditorHtmlEmpty(manualContent)}
                className="btn-primary flex items-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {manualSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <FileEdit className="w-4 h-4" />
                )}
                <span>
                  {manualSaving
                    ? (manualAiGenerateDescription ? 'Generating description…' : 'Saving…')
                    : 'Save as Draft'}
                </span>
              </button>
              <button
                onClick={() => { setManualStatus('published'); handleCreateManual('published'); }}
                disabled={manualSaving || !manualTitle.trim() || isEditorHtmlEmpty(manualContent)}
                className="btn-secondary flex items-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {manualSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Globe className="w-4 h-4" />
                )}
                <span>
                  {manualSaving
                    ? (manualAiGenerateDescription ? 'Generating description…' : 'Publishing…')
                    : 'Publish'}
                </span>
              </button>
              <button
                onClick={() => {
                  cleanupOrphanedMedia(createPendingMediaKeysRef);
                  setShowManualForm(false);
                  clearPendingImages();
                  setShowCreateAIPanel(false);
                  setCreateAIConversationId(null);
                  setCreateAIContext('');
                  setManualDescription('');
                  setManualAiGenerateDescription(false);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hub tab bar */}
      <div className="flex items-center gap-0.5 border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden">
        {([ 'articles', 'collections', 'suggestions' ] as const).map(tab => {
          const labels = { articles: 'Articles', collections: 'Collections', suggestions: 'Article Suggestions' };
          const icons = {
            articles: <FileText className="w-4 h-4" />,
            collections: <Layers className="w-4 h-4" />,
            suggestions: <Sparkles className="w-4 h-4" />,
          };
          return (
            <button
              key={tab}
              onClick={() => {
                setHubTab(tab);
                if (tab === 'suggestions' && suggestions.length === 0 && !suggestionsLoading) {
                  fetchSuggestions();
                }
              }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap ${
                hubTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {icons[tab]}
              {labels[tab]}
              {tab === 'suggestions' && suggestions.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded-full">
                  {suggestions.length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Articles tab */}
      {hubTab === 'articles' && <>
      {/* Upload Section */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <Upload className="w-5 h-5" />
            <span>Upload Files</span>
          </h2>
          <input
            type="file"
            onChange={handleFileSelect}
            className="input-field"
            accept=".pdf,.docx,.txt,.md,.csv,.xlsx,.mp4,.mpeg,.mov,.avi,.flv,.mpg,.webm,.wmv,.3gpp,.m4v,.mkv,video/*"
            multiple
          />
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Supported: PDF, DOCX, TXT, MD, CSV, XLSX, MP4, MOV, WEBM (video — analysis optional)
          </p>
          <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <Info className="w-4 h-4 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              It is highly recommended to segregate article content by topic/part when uploading files. Breaking content into focused, single-topic documents helps AI agents search faster and answer customer questions more accurately.
            </p>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
            <LinkIcon className="w-5 h-5" />
            <span>Scrape URLs</span>
          </h2>
          <textarea
            value={urls}
            onChange={(e) => setUrls(e.target.value)}
            placeholder="Enter URLs (one per line)"
            className="input-field h-24"
            disabled={scrapingUrls}
          />
          {/* Plan-VideoAnalysis.md §C.2 — single global toggle on the URL
              paste card. Visible only when the textarea has at least one
              YouTube URL (Plan §B.1: YouTube is the only Gemini-native source
              we support on URL paste). v1 limitation: only the FIRST YouTube
              URL is analyzed per submit. */}
          {(() => {
            const urlList = urls.split('\n').map(u => u.trim()).filter(u => u);
            const youtubeUrls = urlList.filter(u => isYouTubeUrl(u));
            const hasYouTube = youtubeUrls.length > 0;
            const hasNonYouTubeUrls = urlList.length > youtubeUrls.length;
            if (!hasYouTube && !hasNonYouTubeUrls) return null;
            const planKey = (billing?.plan ?? 'trial') as Plan;
            const limit = billing?.usage_limits?.max_video_analyses
              ?? VIDEO_ANALYSIS_DEFAULT_QUOTA[planKey];
            const used = billing?.usage?.video_analyses ?? 0;
            const remaining = limit < 0 ? '∞' : Math.max(0, limit - used);
            const limitDisplay = limit < 0 ? 'unlimited' : limit.toString();
            // Plan-TextVoicePoolBilling-2026-08 §3 — `max_video_analyses` is
            // retired in pooled mode (video draws text-pool tokens), so the
            // per-analysis counter would quote a dead limit. Hidden, not
            // deleted. Same treatment on the file-upload card below and in
            // RichTextEditor.tsx.
            const pooledBilling = isPooledBilling(billing);
            const checked = urlAnalyzeVideo && hasYouTube;
            return (
              <div className="flex items-start justify-between gap-3 mt-3 px-3 py-2.5 rounded-lg bg-purple-50/40 dark:bg-purple-900/10 border border-purple-200/50 dark:border-purple-800/40">
                <div className="min-w-0">
                  <label
                    className={`flex items-center gap-1.5 text-xs font-medium ${
                      hasYouTube
                        ? 'text-gray-700 dark:text-gray-300'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    <Video className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                    Analyze YouTube video to generate article content
                  </label>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {hasYouTube ? (
                      <>
                        {pooledBilling ? (
                          'Uses your AI text allowance.'
                        ) : (
                          <>
                            Uses 1 of {limitDisplay} monthly video analyses
                            {limit > 0 && ` · ${remaining} remaining`}.
                          </>
                        )}
                        {youtubeUrls.length > 1 && (
                          <span className="block text-amber-600 dark:text-amber-400 mt-0.5">
                            Only the first YouTube URL will be analyzed (v1).
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="flex items-start gap-1 text-amber-600 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        AI analysis supports YouTube URLs and uploaded files only.
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!hasYouTube || scrapingUrls}
                  title={!hasYouTube ? 'AI analysis supports YouTube and uploaded files only.' : undefined}
                  onClick={() => setUrlAnalyzeVideo(v => !v)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    !hasYouTube
                      ? 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-60'
                      : checked
                        ? 'bg-purple-500 cursor-pointer'
                        : 'bg-gray-300 dark:bg-gray-600 cursor-pointer'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      checked ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            );
          })()}
          <button
            onClick={handleScrapeUrls}
            className="btn-primary mt-2 w-full flex items-center justify-center space-x-2"
            disabled={scrapingUrls || !urls.trim()}
          >
            {scrapingUrls ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Scraping URLs...</span>
              </>
            ) : (
              <span>Scrape URLs</span>
            )}
          </button>
        </div>
      </div>

      {/* Knowledge Sources List */}
      <div className="card-hover">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-2xl font-bold gradient-text">Knowledge Articles</h2>
          {knowledge.length > 0 && (
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          )}
          {knowledge.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary transition-colors"
            >
              {selectedIds.size === knowledge.length
                ? <CheckSquare className="w-4 h-4 text-primary" />
                : <Square className="w-4 h-4" />}
              <span>{selectedIds.size === knowledge.length ? 'Deselect All' : 'Select All'}</span>
            </button>
          )}
        </div>

        {/* Bulk action toolbar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-primary/5 dark:bg-primary/10 rounded-xl border border-primary/20">
            <span className="text-sm font-medium text-primary">{selectedIds.size} selected</span>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              <button
                onClick={() => handleBulkAction('publish')}
                disabled={bulkLoading}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Globe className="w-3.5 h-3.5" />
                <span>Publish</span>
              </button>
              <button
                onClick={() => handleBulkAction('draft')}
                disabled={bulkLoading}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 transition-colors"
              >
                <FileEdit className="w-3.5 h-3.5" />
                <span>Draft</span>
              </button>
              <button
                onClick={() => handleBulkAction('delete')}
                disabled={bulkLoading}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
              <div className="w-px h-5 bg-gray-300 dark:bg-gray-600" />
              <button
                onClick={handleBulkHelpCenter}
                disabled={bulkLoading}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                <BookOpen className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Add to</span> <span>Help Center</span>
              </button>
              <div className="relative">
                <button
                  onClick={openBulkCollectionPicker}
                  disabled={bulkLoading}
                  className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Add to</span> <span>Collection</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showBulkCollectionPicker && (
                  <div className="absolute top-full mt-1 right-0 z-20 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1">
                    <button
                      onClick={() => handleBulkCollection(null)}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      Remove from collection
                    </button>
                    {bulkCollections.length > 0 && (
                      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                    )}
                    {bulkCollections.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleBulkCollection(c.id)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        {c.emoji ?? '📚'} {c.name}
                      </button>
                    ))}
                    <button
                      onClick={() => setShowBulkCollectionPicker(false)}
                      className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 border-t border-gray-100 dark:border-gray-700 mt-1"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={handleBulkTransferClick}
                disabled={bulkLoading}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
              >
                <ArrowRightLeft className="w-3.5 h-3.5" />
                <span>Move / Copy</span>
              </button>
              {bulkLoading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-center py-8 text-gray-600 dark:text-gray-400">Loading...</p>
        ) : knowledge.length === 0 ? (
          <div className="py-12 flex flex-col items-center gap-6">
            <div className="text-center">
              <Sparkles className="w-10 h-10 text-primary/60 mx-auto mb-3" />
              <p className="text-gray-700 dark:text-gray-300 font-semibold text-lg">No articles yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Create articles manually or let AI generate your entire Help Center.</p>
            </div>
            <button
              onClick={() => { setShowManualForm(true); }}
              className="btn-primary flex items-center gap-2 px-6 py-3 text-base"
            >
              <Plus className="w-5 h-5" />
              Create Your First Article
            </button>
          </div>
        ) : (() => {
          const filteredArticles = knowledge.filter(ks =>
            ks.title.toLowerCase().includes(searchQuery.toLowerCase())
          );
          return filteredArticles.length === 0 && searchQuery ? (
            <div className="py-8 text-center">
              <Search className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 dark:text-gray-400">No articles found matching "{searchQuery}"</p>
            </div>
          ) : (
          <div className="space-y-3">
            {filteredArticles.map((ks) => (
              <div key={ks.id} onClick={() => toggleSelect(ks.id)} className={`glass rounded-xl p-5 hover:scale-[1.02] transition-all duration-300 cursor-pointer ${selectedIds.has(ks.id) ? 'ring-2 ring-primary' : ''}`}>
                <div className="flex items-start space-x-3">
                  <div className="mt-1 flex-shrink-0 text-gray-400">
                    {selectedIds.has(ks.id)
                      ? <CheckSquare className="w-4 h-4 text-primary" />
                      : <Square className="w-4 h-4" />}
                  </div>
                  <div className={`w-10 h-10 bg-gradient-to-br ${getIconGradient(ks)} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    {getSourceIcon(ks)}
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center flex-wrap gap-1.5">
                      <span>{ks.title}</span>
                      {ks.is_shopify_default && (
                        <span
                          className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 text-xs font-medium inline-flex items-center gap-1"
                          title="Seeded from your Shopify store policies/pages"
                        >
                          <Store className="w-3 h-3" />
                          Shopify Default
                        </span>
                      )}
                    </h3>
                    {ks.content_preview && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 overflow-hidden break-all">
                        {ks.content_preview}
                      </p>
                    )}
                    <div className="flex items-center flex-wrap gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium flex items-center space-x-1 ${
                        (ks.status ?? 'published') === 'published'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                      }`}>
                        {(ks.status ?? 'published') === 'published'
                          ? <><Globe className="w-3 h-3" /><span>Published</span></>
                          : <><FileEdit className="w-3 h-3" /><span>Draft</span></>}
                      </span>
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                        {ks.source_type}
                      </span>
                      {ks.source_type === 'file' && ks.original_filename && (() => {
                        const ext = ks.original_filename!.split('.').pop()?.toUpperCase();
                        if (!ext) return null;
                        const colors: Record<string, string> = {
                          PDF: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
                          DOCX: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
                          DOC: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
                          TXT: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
                          MD: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
                          CSV: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
                          XLSX: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
                          XLS: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
                        };
                        return (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[ext] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                            {ext}
                          </span>
                        );
                      })()}
                      {(ks.processing_status === 'pending' || ks.processing_status === 'processing') && (
                        <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full flex items-center space-x-1">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>Generating summary…</span>
                        </span>
                      )}
                      {ks.has_images && (
                        <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center space-x-1">
                          <Image className="w-3 h-3" />
                          <span>Images</span>
                        </span>
                      )}
                      {ks.has_video && (
                        <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full flex items-center space-x-1">
                          <Video className="w-3 h-3" />
                          <span>Video</span>
                        </span>
                      )}
                      {(ks.status ?? 'published') === 'published' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleHelpCenter(ks); }}
                          className={`text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 transition-colors ${
                            ks.help_center_enabled
                              ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                          title={ks.help_center_enabled ? 'Remove from Help Center' : 'Add to Help Center'}
                        >
                          <BookOpen className="w-3 h-3" />
                          <span>Help Center</span>
                          {ks.help_center_enabled && <span>✓</span>}
                        </button>
                      )}
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatServerDate(ks.created_at)}
                      </span>
                      {ks.source_url && (
                        <a
                          href={ks.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          View Source
                        </a>
                      )}
                    </div>
                    {/* Analytics stats */}
                    {((ks.view_count ?? 0) > 0 || (ks.helpful_count ?? 0) > 0 || (ks.not_helpful_count ?? 0) > 0) && (
                      <div className="flex items-center gap-3 mt-1.5">
                        {(ks.view_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                            <Eye className="w-3 h-3" />
                            {ks.view_count}
                          </span>
                        )}
                        {(ks.helpful_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-green-500 dark:text-green-400">
                            <ThumbsUp className="w-3 h-3" />
                            {ks.helpful_count}
                          </span>
                        )}
                        {(ks.not_helpful_count ?? 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-red-400 dark:text-red-500">
                            <ThumbsDown className="w-3 h-3" />
                            {ks.not_helpful_count}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-end space-x-1 mt-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleView(ks); }}
                        className="text-primary hover:bg-primary/10 p-2 rounded-lg transition-colors"
                        title="View full article"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(ks); }}
                        className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-lg transition-colors"
                        title="Edit article"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTransferClick(ks.id, ks.title); }}
                        className="text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 p-2 rounded-lg transition-colors"
                        title="Move or copy article to another agent"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(ks.id, ks.title); }}
                        className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-lg transition-colors"
                        title="Delete article"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>
      </> /* end articles tab */}

      {/* Collections tab */}
      {hubTab === 'collections' && (
        <CollectionManager agentId={selectedAgent} />
      )}

      {/* Suggestions tab */}
      {hubTab === 'suggestions' && (
        <div className="card-hover space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold gradient-text flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Article Suggestions
            </h2>
            <button
              onClick={fetchSuggestions}
              disabled={suggestionsLoading}
              className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary transition-colors disabled:opacity-50"
            >
              <RotateCcw className={`w-4 h-4 ${suggestionsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {suggestionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse rounded-xl p-4 bg-gray-100 dark:bg-gray-700 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : suggestions.length === 0 ? (
            <div className="py-12 text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="font-medium text-gray-700 dark:text-gray-300">No gaps detected.</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Your Help Center is covering common questions.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map(s => (
                <div
                  key={s.conversation_id}
                  className={`glass rounded-xl p-4 transition-all duration-300 ${
                    dismissingIds.has(s.conversation_id) ? 'opacity-0 scale-95' : 'opacity-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageCircle className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{s.suggested_title}</h3>
                      {s.excerpt && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                          &ldquo;{s.excerpt.slice(0, 120)}{s.excerpt.length > 120 ? '…' : ''}&rdquo;
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{relativeTime(s.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 mt-1">
                      <button
                        onClick={() => handleDismiss(s.conversation_id)}
                        className="text-xs px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => handleCreateFromSuggestion(s)}
                        className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-1.5"
                      >
                        <Sparkles className="w-3 h-3" />
                        Create →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* View Article Modal */}
      {viewingArticle && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${getIconGradient(viewingArticle)} rounded-xl flex items-center justify-center`}>
                  {getSourceIcon(viewingArticle)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold gradient-text">{viewingArticle.title}</h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                      {viewingArticle.source_type}
                    </span>
                    {viewingArticle.has_images && (
                      <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full flex items-center space-x-1">
                        <Image className="w-3 h-3" />
                        <span>Images</span>
                      </span>
                    )}
                    {viewingArticle.has_video && (
                      <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full flex items-center space-x-1">
                        <Video className="w-3 h-3" />
                        <span>Video</span>
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatServerDate(viewingArticle.created_at)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setViewingArticle(null); setViewingPreview(null); }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {viewingArticle.source_url && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Source:</p>
                  <a
                    href={viewingArticle.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all"
                  >
                    {viewingArticle.source_url}
                  </a>
                </div>
              )}
              {viewingArticle.s3_file_url && (
                <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Uploaded File:</p>
                  <a
                    href={encodeURI(viewingArticle.s3_file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-700 dark:text-green-400 hover:underline break-all text-sm"
                  >
                    {encodeURI(viewingArticle.s3_file_url)}
                  </a>
                </div>
              )}
              {viewingArticle.gemini_summary && (
                <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">AI Summary:</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{viewingArticle.gemini_summary}</p>
                </div>
              )}
              {viewingPreview && !viewingArticle.content ? (
                <FilePreview
                  preview_type={viewingPreview.preview_type}
                  content={viewingPreview.content}
                  preview_data={viewingPreview.preview_data}
                />
              ) : (
                (() => {
                  const raw = viewingArticle.content || viewingArticle.content_preview || '';
                  if (isHtmlContent(raw)) {
                    return (
                      <div
                        className="prose dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(raw) }}
                      />
                    );
                  }
                  return (
                    <div className="prose dark:prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={previewComponents}
                      >
                        {raw}
                      </ReactMarkdown>
                    </div>
                  );
                })()
              )}
              {(() => {
                const appended = collectAppendedArticleVideos(
                  viewingArticle.content || viewingArticle.content_preview || '',
                  {
                    media_videos: viewingArticle.media_videos,
                    video_urls: viewingArticle.video_urls,
                  }
                );
                if (!appended.length) return null;
                return (
                  <section className="mt-6">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                      Videos
                    </h3>
                    <div className="space-y-3">
                      {appended.map((v, i) => (
                        <figure
                          key={`av-${i}`}
                          className="not-prose w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                        >
                          <video
                            controls
                            playsInline
                            preload="metadata"
                            poster={v.thumbnail}
                            aria-label={v.label}
                            className="block w-full max-w-full h-auto bg-black"
                          >
                            <source src={withPosterFrame(v.url.replace(/ /g, '%20'))} />
                          </video>
                          {v.label && (
                            <figcaption className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                              {v.label}
                            </figcaption>
                          )}
                        </figure>
                      ))}
                    </div>
                  </section>
                );
              })()}
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => { setViewingArticle(null); setViewingPreview(null); }}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Article Modal */}
      {editingArticle && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold gradient-text">Edit Article</h2>
              <button
                onClick={() => { cleanupOrphanedMedia(editPendingMediaKeysRef); setEditingArticle(null); clearPendingImages(); }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
              {editingArticle.is_shopify_default && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    This article was seeded from your Shopify store's policies/pages.
                    You can edit it freely, but re-syncing will overwrite your changes
                    with the current Shopify content.
                  </span>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Article Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="input-field"
                />
                <div className="flex justify-end mt-1.5">
                  <button
                    type="button"
                    onClick={() => setShowEditAIPanel(p => !p)}
                    className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Generate with AI
                  </button>
                </div>
              </div>
              {/* Edit modal — Generate with AI panel */}
              {showEditAIPanel && (
                <div className="border border-indigo-200 dark:border-indigo-800 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 dark:bg-indigo-900/20">
                    <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Generate with AI</span>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Tone</label>
                      <div className="flex gap-1.5">
                        {(['friendly', 'formal', 'concise'] as const).map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setEditAITone(t)}
                            className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                              editAITone === t
                                ? 'bg-primary text-white border-primary'
                                : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary/50'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                        Context <span className="font-normal text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        value={editAIContext}
                        onChange={e => setEditAIContext(e.target.value)}
                        placeholder="Paste a ticket, describe the issue, or leave blank"
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleEditGenerate}
                      disabled={!editTitle.trim() || editAIGenerating}
                      className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                      {editAIGenerating ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />Generating…</>
                      ) : (
                        <><Sparkles className="w-4 h-4" />Generate</>
                      )}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-start justify-between gap-3 mb-2 flex-col sm:flex-row sm:items-center">
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  {/*
                    Bug 3 — same gate as create-mode: AI description
                    toggle disabled when article content is empty.
                  */}
                  {(() => {
                    const contentEmpty = isEditorHtmlEmpty(editContent);
                    const gated = contentEmpty && !editAiGenerateDescription;
                    return (
                      <label
                        className={`flex items-center gap-2 select-none ${gated ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                        title={gated ? 'Add article content first to generate a description.' : undefined}
                      >
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Auto-generate with AI
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={editAiGenerateDescription}
                          aria-disabled={gated}
                          disabled={gated}
                          onClick={() => setEditAiGenerateDescription(v => !v)}
                          title={gated ? 'Add article content first to generate a description.' : undefined}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed ${
                            editAiGenerateDescription ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              editAiGenerateDescription ? 'translate-x-[18px]' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </label>
                    );
                  })()}
                </div>
                <textarea
                  id="edit-description"
                  value={editAiGenerateDescription ? '' : editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  disabled={editAiGenerateDescription}
                  rows={2}
                  placeholder={editAiGenerateDescription ? '' : 'Briefly describe what this article covers (optional)'}
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none disabled:bg-gray-100 dark:disabled:bg-gray-800/60 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed"
                />
                {editAiGenerateDescription && (
                  <p className="mt-1.5 text-xs text-indigo-600 dark:text-indigo-400 flex items-start gap-1.5">
                    <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <span>AI will regenerate this on save when your content changes or the current description is empty.</span>
                  </p>
                )}
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  A good description helps our AI answer customer questions faster and more accurately. Keep it concise — one sentence is ideal.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Article Content
                </label>
                {/* Toolbar row: Split + Improve with AI */}
                <div className="flex items-center space-x-1 mb-2">
                  {/* Split into Articles button */}
                  <button
                    type="button"
                    onClick={handleSplit}
                    disabled={splitting || isEditorHtmlEmpty(editContent)}
                    className="flex items-center gap-1.5 ml-auto px-2.5 py-1.5 text-xs font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {splitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Splitting… (this may take a moment)
                      </>
                    ) : (
                      <>
                        <Layers className="w-3.5 h-3.5" />
                        Split
                      </>
                    )}
                  </button>
                  {/* Improve with AI dropdown */}
                  <div className="relative" ref={improveDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowImproveDropdown(p => !p)}
                      disabled={improving || isEditorHtmlEmpty(editContent)}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {improving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5" />
                      )}
                      Improve with AI
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {showImproveDropdown && (
                      <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                        {[
                          { label: 'Simplify', type: 'simplify' },
                          { label: 'Expand', type: 'expand' },
                          { label: 'Fix Tone', type: 'fix_tone' },
                          { label: 'Add Steps', type: 'add_steps' },
                          { label: 'Rewrite', type: 'rewrite' },
                        ].map(opt => (
                          <button
                            key={opt.type}
                            type="button"
                            onClick={() => handleImprove(opt.type)}
                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300 transition-colors"
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {editAIPreview ? (
                  <div className="border border-emerald-200 dark:border-emerald-800 rounded-xl overflow-hidden mt-2">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-50 dark:bg-emerald-900/20">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">AI Preview</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleAcceptEditPreview}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={handleRejectEditPreview}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </div>
                    </div>
                    {editAIPreview.title && (
                      <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-emerald-100 dark:border-emerald-800">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Suggested title: </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{editAIPreview.title}</span>
                      </div>
                    )}
                    {editAIPreview.collection && (
                      <div className="px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/10 border-b border-emerald-100 dark:border-emerald-800">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Suggested collection: </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{editAIPreview.collection}</span>
                      </div>
                    )}
                    <div className="p-4 max-h-64 overflow-y-auto">
                      <div className="prose dark:prose-invert max-w-none text-sm">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} urlTransform={(url) => url} components={previewComponents}>{editAIPreview.content}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <RichTextEditor
                      mode="article"
                      // Top-level `agent_id` is the canonical schema; the
                      // `metadata.agent_id` fallback covers legacy docs that
                      // stored it nested. `selectedAgent` is the last-resort
                      // default for the rare case where editingArticle is loaded
                      // before the workspace's agent state hydrates.
                      agentId={editingArticle?.agent_id || editingArticle?.metadata?.agent_id || selectedAgent}
                      content={editContent}
                      onChange={(html) => setEditContent(html)}
                      placeholder="Write your article content..."
                      onMediaUploaded={(key) => editPendingMediaKeysRef.current.add(key)}
                    />
                    {(editAIGenerating || improving) && (
                      <div className="absolute inset-0 bg-white/75 dark:bg-gray-800/75 flex items-center justify-center rounded-xl z-10">
                        <div className="flex items-center gap-2 text-primary">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm font-medium">{improving ? 'Improving…' : 'Generating…'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Credit disclaimer — 🚨 TWIN of the manual-article editor's
                    block above, duplicated VERBATIM; THE TWO MUST MOVE
                    TOGETHER. Legacy-only, same reasoning as there. */}
                {showLegacyCredits && (
                  <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-2">
                    <Info className="w-3 h-3 flex-shrink-0" />
                    AI generation uses Chat credits (1 credit per API call)
                  </p>
                )}
              </div>

              {/* Help Center Settings */}
              <div className="border border-indigo-200 dark:border-indigo-800 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20">
                  <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">Help Center Settings</span>
                </div>
                <div className="p-4 space-y-4">
                  {/* Collection + Section */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Collection {hcCollectionsLoading && <Loader2 className="inline w-3 h-3 animate-spin ml-1" />}
                    </label>
                    <select
                      value={editHCCollectionId}
                      onChange={e => setEditHCCollectionId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">— No collection —</option>
                      {hcCollections.map(c => (
                        <option key={c.id} value={c.id}>{c.emoji ?? ''} {c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Slug */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Article Slug
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editHCSlug}
                        // Permissive while typing (a trailing hyphen must
                        // survive), strict on blur — same fix as the help
                        // center slug field. Strict-on-keystroke made a
                        // hyphenated article slug untypeable here too.
                        onChange={e => setEditHCSlug(slugifyTyping(e.target.value))}
                        onBlur={() => setEditHCSlug(slugifyStrict(editHCSlug))}
                        placeholder="auto-generated-from-title"
                        className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setEditHCSlug(slugify(editTitle))}
                        className="px-2 py-2 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg whitespace-nowrap"
                        title="Auto-generate from title"
                      >
                        Auto
                      </button>
                    </div>
                    {articlePublicUrl && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">
                          {articlePublicUrl}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            // Same string the user sees above — one source of
                            // truth, so the copied link can't drift from it.
                            try { await navigator.clipboard.writeText(articlePublicUrl); } catch {}
                            setCopiedArticleLink(true);
                            setTimeout(() => setCopiedArticleLink(false), 2000);
                          }}
                          title="Copy link"
                          className="flex-shrink-0"
                        >
                          {copiedArticleLink
                            ? <span className="text-xs text-green-500">Copied!</span>
                            : <Clipboard className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* SEO (collapsible) */}
                  <div>
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => setEditSeoExpanded(p => !p)}
                        className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                      >
                        {editSeoExpanded
                          ? <ChevronDown className="w-3.5 h-3.5" />
                          : <ChevronRight className="w-3.5 h-3.5" />}
                        SEO Settings
                      </button>
                      <button
                        type="button"
                        onClick={handleAutoSEO}
                        disabled={editSeoGenerating || isEditorHtmlEmpty(editContent)}
                        className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        {editSeoGenerating
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Sparkles className="w-3 h-3" />}
                        Auto-fill
                      </button>
                    </div>
                    {editSeoExpanded && (
                      <div className="mt-3 space-y-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Meta Title
                          </label>
                          <input
                            type="text"
                            value={editHCMetaTitle}
                            onChange={e => setEditHCMetaTitle(e.target.value)}
                            placeholder={editTitle}
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Meta Description
                          </label>
                          <textarea
                            value={editHCMetaDesc}
                            onChange={e => setEditHCMetaDesc(e.target.value.slice(0, 160))}
                            rows={3}
                            placeholder="Brief description for search engines"
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                          />
                          <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
                            {editHCMetaDesc.length}/160
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Save as:</span>
                <button
                  onClick={() => { setEditStatus('published'); }}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    editStatus === 'published'
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                  }`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>Published</span>
                </button>
                <button
                  onClick={() => { setEditStatus('draft'); }}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    editStatus === 'draft'
                      ? 'bg-yellow-500 text-white border-yellow-500'
                      : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-yellow-400'
                  }`}
                >
                  <FileEdit className="w-3.5 h-3.5" />
                  <span>Draft</span>
                </button>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  className="btn-primary flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {editSaving && (
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  )}
                  <span>
                    {editSaving
                      ? (editAiGenerateDescription ? 'Generating description…' : 'Saving…')
                      : 'Save Changes'}
                  </span>
                </button>
                <button
                  onClick={() => {
                    // Cancel stays clickable mid-save so the user can bail out;
                    // the in-flight request is allowed to settle on its own (its
                    // success handler is a no-op once editingArticle is null on
                    // re-render), and editSaving will be reset by the handler's
                    // finally block regardless.
                    cleanupOrphanedMedia(editPendingMediaKeysRef);
                    setEditingArticle(null);
                    clearPendingImages();
                    setEditSaving(false);
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Metadata Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold gradient-text">Upload Files</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {pendingUploadFiles.length} file{pendingUploadFiles.length > 1 ? 's' : ''} selected — set title and description before uploading
                </p>
              </div>
              <button
                onClick={() => { setShowUploadModal(false); setPendingUploadFiles([]); setFileMetadataList([]); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
              {pendingUploadFiles.map((file, i) => (
                <div key={i} className="glass rounded-xl p-4 space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-9 h-9 bg-gradient-to-br from-primary to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{file.name}</p>
                      <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    {/* Status toggle */}
                    <div className="flex flex-wrap items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => updateFileMeta(i, 'status', 'published')}
                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                          fileMetadataList[i]?.status === 'published'
                            ? 'bg-green-600 text-white border-green-600'
                            : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <Globe className="w-3 h-3" />
                        <span>Published</span>
                      </button>
                      <button
                        onClick={() => updateFileMeta(i, 'status', 'draft')}
                        className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                          fileMetadataList[i]?.status === 'draft'
                            ? 'bg-yellow-500 text-white border-yellow-500'
                            : 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        <FileEdit className="w-3 h-3" />
                        <span>Draft</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title <span className="text-red-400">*</span></label>
                    <input
                      type="text"
                      value={fileMetadataList[i]?.title ?? ''}
                      onChange={e => updateFileMeta(i, 'title', e.target.value)}
                      className="input-field text-sm"
                      placeholder="Article title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description <span className="text-gray-400">(optional — used as search index)</span>
                    </label>
                    <input
                      type="text"
                      value={fileMetadataList[i]?.description ?? ''}
                      onChange={e => updateFileMeta(i, 'description', e.target.value)}
                      className="input-field text-sm"
                      placeholder="What does this document cover?"
                    />
                  </div>
                  {/* Doc/video toggles are mutually exclusive — show the
                      "Generate AI Summary" toggle ONLY for non-video files
                      (PDF / DOCX / TXT / etc.) and the "Analyze video"
                      toggle ONLY for video files. Per user feedback:
                      "deactivate the Video toggle whenever a user uploads
                      a docs file, while deactivate the docs analysis toggle
                      whenever a user uploads a video file." We HIDE rather
                      than disable — keeps the row uncluttered, no irrelevant
                      controls competing for attention. */}
                  {!isVideoFile(file) && (
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Generate AI Summary</label>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Uses AI to create a detailed summary</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => updateFileMeta(i, 'skip_summary', !fileMetadataList[i]?.skip_summary)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          !fileMetadataList[i]?.skip_summary
                            ? 'bg-primary'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            !fileMetadataList[i]?.skip_summary ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Plan-VideoAnalysis.md §C.2 — per-video "Analyze" toggle.
                      Renders only for video files (counterpart to the
                      "Generate AI Summary" toggle above). File-size guard
                      per Plan §B.2. */}
                  {isVideoFile(file) && (() => {
                    const planKey = (billing?.plan ?? 'trial') as Plan;
                    const capMb = VIDEO_ANALYSIS_PLAN_CAP_MB[planKey];
                    const fileMb = file.size / (1024 * 1024);
                    const tooLarge = fileMb > capMb;
                    const next = VIDEO_ANALYSIS_NEXT_TIER[planKey];
                    const sizeTip = tooLarge
                      ? next
                        ? `Video too large to analyze on ${PLAN_LABELS[planKey]}. Upgrade to ${PLAN_LABELS[next.plan]} for ${next.cap} MB limit.`
                        : `Video exceeds the ${capMb} MB analysis limit.`
                      : null;
                    const limit = billing?.usage_limits?.max_video_analyses
                      ?? VIDEO_ANALYSIS_DEFAULT_QUOTA[planKey];
                    const used = billing?.usage?.video_analyses ?? 0;
                    const remaining = limit < 0
                      ? '∞'
                      : Math.max(0, limit - used);
                    const limitDisplay = limit < 0 ? 'unlimited' : limit.toString();
                    // Pooled mode retires `max_video_analyses` — see the URL
                    // paste card above. The FILE SIZE cap line stays either
                    // way; it is a per-file Gemini constraint, not a quota.
                    const pooledBilling = isPooledBilling(billing);
                    const checked = !!fileMetadataList[i]?.analyze_video && !tooLarge;
                    return (
                      <div className="flex items-start justify-between gap-3 pt-2 border-t border-white/5 dark:border-gray-700/40">
                        <div className="min-w-0">
                          <label
                            className={`flex items-center gap-1.5 text-xs font-medium ${
                              tooLarge
                                ? 'text-gray-400 dark:text-gray-500'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <Video className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" />
                            Analyze video to generate article content
                          </label>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            {tooLarge ? (
                              <span className="text-amber-600 dark:text-amber-400 flex items-start gap-1">
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                {sizeTip}
                              </span>
                            ) : (
                              <>
                                {pooledBilling ? (
                                  'Uses your AI text allowance.'
                                ) : (
                                  <>
                                    Uses 1 of {limitDisplay} monthly video analyses
                                    {limit > 0 && ` · ${remaining} remaining`}.
                                  </>
                                )}
                                {' '}File: {fileMb.toFixed(1)} MB / {capMb} MB cap.
                              </>
                            )}
                          </p>
                        </div>
                        <button
                          type="button"
                          disabled={tooLarge}
                          title={tooLarge ? sizeTip ?? '' : undefined}
                          onClick={() =>
                            updateFileMeta(i, 'analyze_video', !fileMetadataList[i]?.analyze_video)
                          }
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            tooLarge
                              ? 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed opacity-60'
                              : checked
                                ? 'bg-purple-500 cursor-pointer'
                                : 'bg-gray-300 dark:bg-gray-600 cursor-pointer'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              checked ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex space-x-3">
              <button
                onClick={handleUploadSubmit}
                disabled={submittingUpload || fileMetadataList.some(m => !m.title.trim())}
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
              >
                {submittingUpload ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /><span>Uploading…</span></>
                ) : (
                  <><Upload className="w-4 h-4" /><span>Upload {pendingUploadFiles.length} File{pendingUploadFiles.length > 1 ? 's' : ''}</span></>
                )}
              </button>
              <button
                onClick={() => { setShowUploadModal(false); setPendingUploadFiles([]); setFileMetadataList([]); }}
                disabled={submittingUpload}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan-VideoAnalysis.md §B.6 — sync HTTP, ~30s wait. Block UI with a
          dismissible-ish overlay so the user understands what's happening
          and doesn't navigate away mid-call. Closing it cancels NOTHING (the
          backend keeps running) — we just hide the curtain so the user can
          continue with other work; the editor will still pop open when the
          analyze finishes. */}
      {analyzingVideo && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="analyze-video-overlay-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white animate-pulse" />
            </div>
            <h3
              id="analyze-video-overlay-title"
              className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white"
            >
              Analyzing video with AI
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Gemini is generating structured article content from your video.
              This usually takes about 30 seconds.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-purple-600 dark:text-purple-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Working…</span>
            </div>
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              The editor will open with the generated content when it&rsquo;s done.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeHub;